/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *|
 *| IBM Cognos Products: Dashboard
 *|
 *| (C) Copyright IBM Corp. 2017, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore',
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'./extendedVis/SchematicVis'
], function(
	_,
	Class,
	SchematicVis
) {

	/**
	* @param {number} value - input number for the property
	* @param {Boolean} true iff the value is a float number or null
	*/
	const validateNumIsFloatOrNull = value => {
		return !(isNaN(value) || isNaN(Number.parseFloat(value))) || (value === null);
	};

	const minMaxValidationOptions = {
		checkForValidValue: validateNumIsFloatOrNull,
		'supportsValidationChecks': true,
		'validationInfo': {
			supportsFloats: true,
			messageId: 'customRangeErrorMessage'
		}
	};

	const SHORT_EFFECT_DURATION = 100;  // CADBC-990 (Formerly 350)
	const LONG_EFFECT_DURATION = 100;    // CADBC-990 (Formerly 500)

	const DEFAULT_CUSTOM_VIS_CONFIGURATION = {
		'customVis':{
			'isCustomVis': true,
			'isRecommendable': false,
			'aggregateAndSortOnClient':true,
			'queryHints':{
				'multiEdgeSort':true
			},
			'includeAllProperties': true,
			'ignoreElementColor': true,
			'listenForPropChanges': [],
			'slots': {
				'color': {
					'sort': 'asc',
					'icon': 'colorBy'
				},
				'repeatHorizontal': {
					'icon': 'multiplier_h',
					'sort': 'asc'
				},
				'repeatVertical': {
					'icon': 'multiplier_v',
					'sort': 'asc'
				}
			},
			'properties': {
				'widget.data.truncate': {
					active: false,
					defaultValue: true
				},
				'widget.iterator': {
					active: false,
					defaultValue: false
				},
				'colors.series': {
					'defaultValue': false
				}
			},
			'bundleInclude': [],
			'config': {
				'include': [
					'widget.data.truncate',
					'widget.iterator'
				]
			}
		}
	};

	const DEFAULT_SCHEMATIC_VIS_CONFIGURATION = {
		'customVis': {
			'isSchematic': true,
			'disablePan': true,
			'isRecommendable': false,
			'doesNotSupportCustomGroupAction': true,
			'renderWithoutCompletingMapping':true,
			'cursor': ['grab', '-moz-grab', '-webkit-grab'],
			'thumbnail': {
				'enabled': false,
				'type': 'img'
			},
			'listenForPropChanges': [
				'schematic',
				'views.hidden',
				'showPreview',
				'region.visible',
				'point.visible'
			],
			'dataRowLimit': 30000,
			'dataRowLimitIE': 3000,
			'slots': {
				'locations': {
					'subtype': 'location',
					'icon': 'location'
				},
				'locationColor': {
					'icon': 'heat_map'
				},
				'pointLocations': {
					'icon': 'location',
					'subtype': 'location'
				},
				'pointSize': {
					'icon': 'size_by',
					'multiMeasure':true
				},
				'pointColor': {
					'icon': 'point_heat',
					'tags':[
						'ca.dashboard.repeatHere=true'
					]
				}
			},
			'propertiesToSetBeforeData': [
				'widget.data.truncate'
			],
			'groupedProperty': {
				'widget.legend.color': ['widget.legend.titleColor'],
				'widget.legend.font': ['widget.legend.titleFont']
			},
			'propertyPrefixes': {
				'locationColor': 'widget.legend',
				'pointColor': 'widget.legend',
				'pointSize': 'widget.legend'
			},
			'properties':{
				'widget.data.truncate': {
					active: false,
					defaultValue: true
				},
				'widget.iterator': {
					active: false,
					defaultValue: false
				},
				'debug.mode': {
					active: false,
					defaultValue: false
				},
				'schematicLibrary': {
					active: false
				},
				'backgroundColor': {
					active: false
				},
				'showPreview': {
					active: false
				},
				'region.visible': {
					active: false
				},
				'point.visible': {
					active: false
				},
				'widget.legend.color':{
					'colorClass': true,
					'addButton': false,
					'displayPos': 45
				},
				'widget.legend.titleColor': {
					'colorClass': true,
					'active': false
				}
			},
			'supportsLayers': true,
			'layerDescriptions':[
				{
					id: 'data.region',
					colorRelatedProps: {
						transparencyProp: 'region.transparency',
						defaultPaletteValue: 'colorPalette0',
						sectionNameId: 'sectionName_regionLayer'
					},
					visibleLayerProps: [
						'region.visible',
						'region.transparency'
					]
				},
				{
					id: 'data.point',
					colorRelatedProps: {
						transparencyProp: 'point.transparency',
						defaultPaletteValue: 'colorPalette1',
						sectionNameId: 'sectionName_pointLayer'
					},
					visibleLayerProps: [
						'point.visible',
						'point.transparency'
					]
				}
			],
			'supportsMaintainAxisScale': true,
			'ignoreElementColor': true,
			'isMultilayersWidget': true,
			'binning': {
				'auto': false
			},
			'config': {
				'include': [
					'widget.data.truncate',
					'widget.iterator',
					'debug.mode',
					'point.visible',
					'region.visible',
					'showPreview',
					'optimizeSize',
					'backgroundColor',
					'widget.legend.backgroundColor',
					'schematicLibrary',
					'schematic',
					'zoomView',
					'zoomData',
					'viewTransparency',
					'enableViewControl',
					'enableLayerControl',
					'region.transparency',
					'point.transparency',
					'point.defaultSize',
					'point.sizeScaling',
					'point.minSize',
					'point.maxSize',
					'point.staticZeroValueSize',
					'point.autoScaleFactor',
					'widget.legend.display',
					'widget.legend.position',
					'widget.legend.size',
					'locationColor.title',
					'pointColor.title',
					'pointSize.title',
					'widget.legend.titleVisible',
					'widget.legend.titleColor',
					'widget.legend.font',
					'widget.legend.color',
					'colors.location.cat',
					'colors.location.cont',
					'colors.point.cat',
					'colors.point.cont',
					'widget.legend.splitter',
					'widget.legend.titleFont',
					'widget.legend.fontSize',
					'views.hidden'
				],
				override: {}
			}
		}
	};

	const customVisConfigurations = {};

	var VIPRConfig = Class.extend({

		// --- CONFIGURATIONS

		configurations:{
			'com.ibm.vis.rave2heat':{
				'type': 'Heatmap',
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges':[
					'widget.legend.size'
				],
				'slots':{
					'categories':{
						'sort':'asc',
						'icon':'row_heading',
						'tags':['badge'],
						'mldTitleProperty': 'itemAxis.title'
					},
					'series':{
						'sort':'asc',
						'icon':'column_heading',
						'tags':['badge'],
						'mldTitleProperty': 'valueAxis.title'
					},
					'points':{
						'icon':'points'
					},
					'color':{
						'icon':'heat_map',
						'mldTitleProperty': 'color.title',
						'tags': ['canUseToColor']  //canUseToColor indicates that smart color feature could be applicable to the data item in this slot
					},
					'size':{
						'icon':'size_by',
						'mldTitleProperty': 'size.title'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'propertyPrefixes': {
					'dataPoint': 'itemAxis.ticks.labels',
					'categories': 'valueAxis.title',
					'series': 'itemAxis.title',
					'color': 'widget.legend',
					'size': 'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font']
				},
				'properties':{
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'color.title': {
						'multilingual': true
					},
					'size.title': {
						'multilingual': true
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'border.width': {
						'active': false
					},
					'widget.legend.size': {
						'active': false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'gridLines.visible',
						'itemAxis.title.visible',
						'valueAxis.title.visible',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.labels.color',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.title.font',
						'valueAxis.title.color',
						'itemAxis.title',
						'valueAxis.title',
						'itemAxis.labels.layoutMode',
						'itemAxis.alignment', //TODO:'axis.x.alignment',
						'gridlineColor',
						'itemAxis.ticks.color',
						'valueAxis.ticks.color',
						'labelColor',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'optimizeSize',
						'border.width',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.titleColor',
						'widget.legend.font',
						'widget.legend.titleFont',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'colors.color',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'color.title',
						'size.title'
					],
					'exclude':[

					]
				},
				'usages':[
					'Relationships',
					'Comparison'
				],
				'supportsMaintainAxisScale': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2bundlearea':{
				'type': 'Area',
				'aggregateAndSortOnClient':true,
				'thumbnail': {
					'properties': {
						'itemAxis.gridLines.visible': true,
						'valueAxis.gridLines.visible': true
					}
				},
				'listenForPropChanges':[
					'widget.legend.size'
				],
				'slots':{
					'series':{
						'sort':'asc',
						'useInTopBottomQueries':false,
						'stackValuesOnAxis': true,
						'tags':[
							'ca.dashboard.repeatHere=true',
							'canUseToColor'
						],
						'icon':'colorBy',
						'mldTitleProperty': 'series.title'
					},
					'categories':{
						'optional':false,
						'selectable':false,
						'sort':'asc',
						'icon':'xAxis',
						'tags': ['badge'],
						'mldTitleProperty': 'itemAxis.title'
					},
					'values':{
						'sortable':true,
						'multiMeasure':true,
						'clientSideAggregatedAndSortInfo':{
							'mappedSlotCondition':[
								'series'
							],
							'groupOnColumnId':'categories'
						},
						'icon':'values',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.title'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				shouldMultiPointSelectionSummarize: true,
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'propertyPrefixes': {
					'dataPoint': 'itemAxis.ticks.labels',
					'values': 'valueAxis.title',
					'categories': 'itemAxis.title',
					'series': 'widget.legend',
					'legend': 'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font']
				},
				'properties':{
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'series.title': {
						'multilingual': true
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'valueAxis.zeroOrigin':{
						'active':false,
						'defaultValue':true,
						'isReadOnly': true
					},
					'area.opacity': {
						'active': false
					},
					'area.borderWidth':{
						'active': false
					},
					'valueAxis.minValue': minMaxValidationOptions,
					'valueAxis.maxValue': minMaxValidationOptions,
					'widget.legend.size': {
						'active': false
					},
					'colors.series': {
						'defaultValue': false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'valueAxis.minValue',
						'valueAxis.maxValue',
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'itemAxis.title.visible',
						'valueAxis.title.visible',
						'valueAxis.zeroOrigin',
						'valueAxis.logarithmic',
						'gridLines.visible',
						'itemAxis.labels.layoutMode',
						'transpose',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.labels.color',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.title.font',
						'valueAxis.title.color',
						'itemAxis.title',
						'valueAxis.title',
						'gridlineColor',
						'itemAxis.ticks.color',
						'valueAxis.ticks.color',
						'labelColor',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'area.interpolate',
						'data.handling',
						'optimizeSize',
						'area.borderColor',
						'area.borderWidth',
						'area.opacity',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleFont',
						'widget.legend.titleColor',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'colors.series',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'series.title',
						'valueAxis.tickInterval'
					],
					'exclude':[
					]
				},
				'usages':[
					'Trend'
				],
				'supportsMaintainAxisScale': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2comet': {
				'type': 'DriverAnalysis',
				'isRecommendable': false,
				'noDataQuery': true,
				'doesNotSupportSortAction': true,
				'doesNotSupportTopBottomAction': false,
				'doesNotSupportFormatAction': true,
				'doesNotSupportCustomGroupAction': true,
				'useFPDAsInsightsInput' : true,
				'ignoreElementColor': true,
				'doesNotSupportOLAP': true,
				'supportsPossibleKeyDrivers': true,
				'hideAggTypeInAxisLabels': true,
				'doesNotSupportAnnotations': true,
				'enableCustomDataSelection': false,
				'thumbnail': {
					'properties': {
						'valueAxis.line.visible': true,
						'valueAxis.ticks.visible': true
					}
				},
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'slots': {
					'values': {
						icon: 'target',
						'actions': [
							'TopKeyDriversAction',
							'PossibleKeyDriverAction'
						],
						'coachMark': {
							'titleResource': 'coachMarkTitleSpiralTarget',
							'contentResource': 'coachMarkContentSpiralTarget',
							'mappedOnly': true,
							'showPopover': true
						}
					}
				},
				'properties':{
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'optimizeSize':{
						'defaultValue': true
					},
					'widget.legend.font': {
						active: false
					},
					'widget.legend.size': {
						'active': false
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					}
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'contrast.axes.color',
						'contrast.text.color',
						'axis.title.visible',
						'axis.indicators.visible',
						'axis.labels.visible',
						'target.title.visible',
						'valueAxis.zeroOrigin',
						'target.title.color',
						'axis.color',
						'axis.label.color',
						'axis.title.color',
						'oneWay.color',
						'twoWay.color',
						'combination.color',
						'optimizeSize',
						'target.stroke.color',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'widget.legend.size'
					],
					'exclude':[

					]
				},
				'usages':[
					'Advanced'
				],
				'supportsMaintainAxisScale': false,
				'binning': {
					'auto': false
				}
			},
			'com.ibm.vis.spiral': {
				'type': 'Spiral',
				'isRecommendable': false,
				'noDataQuery': true,
				'supportsPossibleKeyDrivers': true,
				'doesNotSupportAnnotations': true,
				'doesNotSupportSortAction': true,
				'doesNotSupportTopBottomAction': false,
				'doesNotSupportFormatAction': true,
				'doesNotSupportOLAP': true,
				'doesNotSupportCustomGroupAction': true,
				'enableCustomDataSelection': true,
				'ignoreElementColor': true,
				'hideAggTypeInAxisLabels': true,
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'slots': {
					'values': {
						icon: 'target',
						'actions': [
							'TopKeyDriversAction',
							'PossibleKeyDriverAction'
						],
						'coachMark': {
							'titleResource': 'coachMarkTitleSpiralTarget',
							'contentResource': 'coachMarkContentSpiralTarget',
							'mappedOnly': true,
							'showPopover': true
						},
						'mldTitleProperty': 'valueAxis.title'
					}
				},
				'properties':{
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'optimizeSize':{
						'defaultValue': true
					},
					'effect.duration': {
						'active': false
					}
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'targetVisibility',
						'effect.duration',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'tableAreaBackground',
						'tableContentBackground',
						'ring4Color',
						'ring3Color',
						'ring2Color',
						'ring1Color',
						'ring0Color',
						'bullseyeColor',
						'contrastText',
						'valueAxis.title',
						'optimizeSize'
					],
					'exclude':[
					]
				},
				'usages':[
					'Advanced'
				],
				'supportsMaintainAxisScale': false,
				'binning': {
					'auto': false
				}
			},
			'com.ibm.vis.decisiontree':{
				'type':'DecisionTree',
				'isRecommendable':false,
				'noDataQuery': true,
				'useFPDAsInsightsInput': true, /*Use Fast Predict Key driver response as chart insights request input*/
				'supportsPossibleKeyDrivers': true,
				'doesNotSupportSortAction': true,
				'doesNotSupportTopBottomAction': true,
				'doesNotSupportFormatAction': true,
				'doesNotSupportAnnotations': true,
				'doesNotSupportOLAP': true,
				'doesNotSupportCustomGroupAction': true,
				'enableCustomDataSelection': true,
				'ignoreElementColor': true,
				'shouldLogFPDInsightMessages': true,
				'hideAggTypeInAxisLabels': true,
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont']
				},
				'slots': {
					'values': {
						icon: 'target',
						'actions': [
							'PossibleKeyDriverAction',
							'text'
						],
						'coachMark': {
							'titleResource': 'coachMarkTitleSpiralTarget',
							'contentResource': 'coachMarkContentSpiralTarget',
							'mappedOnly': true,
							'showPopover': true
						},
						'mldTitleProperty': 'values.title'
					}
				},
				'propertyPrefixes': {
					'legend': 'widget.legend'
				},
				'listenForPropChanges': [
					'treeMode',
					'topBottomTargetValue',
					'widget.legend.size'
				],
				'properties':{
					'values.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'format.compact':{
						'active':false
					},
					'effect.duration': {
						'active': false
					},
					'topBottomN': {
						'active': false
					},
					'topBottomTargetValue': {
						'active':false
					},
					'actions': {
						'active': false,
						'defaultValue': 'showTree'
					},
					'showInsights': {
						active: false
					},
					'treeMode': {
						active: false
					},
					'widget.legend.size': {
						'active': false
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					}
				},
				tabs: {
					default: 'showTree',
					entries: [{
						id: 'showSunburst',
						resource: 'decisionSunburst'
					},{
						id: 'showTree',
						resource: 'decisionTree'
					},{
						id: 'showRules',
						resource: 'decisionRules'
					}]
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'showMinimap',
						'actions',
						'showInsights',
						'treeMode',
						'topBottomN',
						'topBottomTargetValue',
						'effect.duration',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'custom_cont',
						'custom_cat',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'values.title',
						'optimizeSize'
					],
					'exclude':[
					]
				},
				'usages':[
					'Advanced'
				],
				'supportsMaintainAxisScale': false,
				'binning': {
					'auto': false
				}
			},
			'com.ibm.vis.sunburst':{
				'type':'Sunburst',
				'isRecommendable':false,
				'noDataQuery': true,
				'supportsPossibleKeyDrivers': true,
				'ignoreElementColor': true,
				'doesNotSupportSortAction': true,
				'doesNotSupportTopBottomAction': true,
				'doesNotSupportFormatAction': true,
				'doesNotSupportAnnotations': true,
				'doesNotSupportOLAP': true,
				'doesNotSupportCustomGroupAction': true,
				'enableCustomDataSelection': true,
				'shouldLogFPDInsightMessages': true,
				'hideAggTypeInAxisLabels': true,
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont']
				},
				'slots': {
					'values': {
						icon: 'target',
						'actions': [
							'PossibleKeyDriverAction',
							'text'
						],
						'coachMark': {
							'titleResource': 'coachMarkTitleSpiralTarget',
							'contentResource': 'coachMarkContentSpiralTarget',
							'mappedOnly': true,
							'showPopover': true
						},
						'mldTitleProperty': 'values.title'
					}
				},
				'propertyPrefixes': {
					'legend': 'widget.legend'
				},
				'listenForPropChanges': [
					'treeMode',
					'topBottomTargetValue',
					'widget.legend.size'
				],
				'properties':{
					'values.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'active': false
					},
					'topBottomN': {
						'active': false
					},
					'topBottomTargetValue': {
						'active':false
					},
					'actions': {
						'active': false,
						'defaultValue': 'showSunburst'
					},
					'showInsights': {
						active: false
					},
					'treeMode': {
						active: false
					},
					'showMinimap': {
						active: false
					},
					'widget.legend.size': {
						'active': false
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					}
				},
				tabs: {
					default: 'showSunburst',
					entries: [{
						id: 'showSunburst',
						resource: 'decisionSunburst'
					},{
						id: 'showTree',
						resource: 'decisionTree'
					},{
						id: 'showRules',
						resource: 'decisionRules'
					}]
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'showMinimap',
						'actions',
						'showInsights',
						'treeMode',
						'topBottomN',
						'topBottomTargetValue',
						'effect.duration',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleFont',
						'widget.legend.titleColor',
						'custom_cont',
						'custom_cat',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'values.title',
						'optimizeSize'
					],
					'exclude':[
					]
				},
				'usages':[
					'Advanced'
				],
				'supportsMaintainAxisScale': false,
				'binning': {
					'auto': false
				}
			},
			'com.ibm.vis.rave2bundletreemap':{
				'type': 'Treemap',
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'slots':{
					'categories':{
						'icon':'level',
						'recommendMulti':true,
						'sort':'asc'
					},
					'size':{
						'icon':'size_by',
						'sortable':false,
						'mldTitleProperty': 'size.title'
					},
					'color':{
						'icon':'heat_map',
						'supportsMaintainAxisScale': true,
						'mldTitleProperty': 'color.title',
						'tags': ['canUseToColor']
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'propertyPrefixes': {
					'dataPoint': 'labels',
					'categories': 'labels',
					'size': 'widget.legend',
					'color': 'widget.legend',
					'legend': 'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont']
				},
				'properties':{
					'color.title': {
						'multilingual': true
					},
					'size.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'active': false,
						'defaultValue': LONG_EFFECT_DURATION
					},
					'widget.legend.size': {
						'active': false
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'contrast.label.color': {
						'defaultValue': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'leaf.labels.visible',
						'labels.color',
						'labels.font',
						'contrast.label.color',
						'label.shadow',
						'effect.duration',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleFont',
						'widget.legend.titleColor',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'colors.categories',
						'colors.color',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'color.title',
						'color.title.visible',
						'size.title',
						'optimizeSize',
						'renderingMode'
					],
					'exclude':[
					]
				},
				'usages':[
					'PartsToWhole'
				],
				'supportsMaintainAxisScale': false,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2bundlepie':{
				'type': 'Pie',
				'disablePan':true,
				'isRecommendable':false,
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'slots':{
					'categories':{
						'icon':'pie_category_slice',
						'mldTitleProperty': 'categories.title'
					},
					'values':{
						'sort':'asc',
						'icon':'values'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'propertyPrefixes': {
					'dataPoint': 'labels',
					'values': 'labels',
					'categories': 'widget.legend',
					'legend': 'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont']
				},
				'properties':{
					'categories.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'active': false,
						'defaultValue': LONG_EFFECT_DURATION
					},
					'border.width':{
						'active': false
					},
					'donutRadius': {
						active: false
					},
					'widget.legend.size': {
						active: false
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'contrast.label.color': {
						'defaultValue': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'donutRadius',
						'label.percentage',
						'labels.visible',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'labelLocation',
						'effect.duration',
						'labels.color',
						'labels.font',
						'labels.format',
						'contrast.label.color',
						'label.shadow',
						'border.width',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'colors.categories',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'categories.title',
						'optimizeSize'
					],
					'exclude':[
						'sort'
					]
				},
				'usages':[
					'PartsToWhole'
				],
				'supportsMaintainAxisScale': false,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.ravebubble':{
				'type': 'Bubble',
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'thumbnail': {
					'properties': {
						'itemAxis.line.visible': true,
						'valueAxis.line.visible': true,
						'itemAxis.gridLines.visible': true,
						'valueAxis.gridLines.visible': true
					}
				},
				'slots':{
					'color':{
						'optional':true,
						'sort':'asc',
						'icon':'colorBy',
						'supportsMaintainAxisScale': true,
						'mldTitleProperty': 'color.title',
						'tags': ['canUseToColor']
					},
					'x':{
						'optional':false,
						'icon':'xAxis',
						'tags':['badge'],
						'mldTitleProperty': 'itemAxis.title',
						'supportsMaintainAxisScale': true,
						'supportSortActionForMeasures': false
					},
					'y':{
						'optional':false,
						'icon':'yAxis',
						'tags':['badge'],
						'mldTitleProperty': 'valueAxis.title',
						'supportsMaintainAxisScale': true,
						'supportSortActionForMeasures': false
					},
					'size':{
						'optional':false,
						'icon':'size_by',
						'mldTitleProperty': 'size.title'
					},
					'categories':{
						'optional':true,
						'icon':'bubbles'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'propertyPrefixes': {
					'dataPoint': 'itemAxis.ticks.labels',
					'x': 'itemAxis.title',
					'y': 'valueAxis.title',
					'size': 'widget.legend',
					'color': 'widget.legend',
					'legend': 'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color','labels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font','labels.font']
				},
				'properties':{
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'color.title': {
						'multilingual': true
					},
					'size.title': {
						'multilingual': true
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'active': false,
						'defaultValue': LONG_EFFECT_DURATION
					},
					'border.width': {
						'active': false
					},
					'bubble.value.smallest.size': {
						'active': false
					},
					'bubble.value.biggest.size': {
						'active': false
					},
					'valueAxis.minValue': minMaxValidationOptions,
					'valueAxis.maxValue': minMaxValidationOptions,
					'itemAxis.minValue': minMaxValidationOptions,
					'itemAxis.maxValue': minMaxValidationOptions,
					'widget.legend.size': {
						active: false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'contrast.label.color': {
						'defaultValue': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'valueAxis.minValue',
						'valueAxis.maxValue',
						'itemAxis.minValue',
						'itemAxis.maxValue',
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'gridLines.visible',
						'labels.visible',
						'itemAxis.title.visible',
						'valueAxis.title.visible',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.labels.color',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.title.font',
						'valueAxis.title.color',
						'valueAxis.logarithmic',
						'itemAxis.title',
						'valueAxis.title',
						'gridlineColor',
						'itemAxis.ticks.color',
						'valueAxis.ticks.color',
						'itemAxis.labels.layoutMode',
						'labelColor',
						'contrast.label.color',
						'border.width',
						'border.color',
						'bubble.value.smallest.size',
						'bubble.value.biggest.size',
						'label.shadow',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'symbolShape',
						'effect.duration',
						'optimizeSize',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'labels.font',
						'labels.color',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'colors.categories',
						'colors.color',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'color.title',
						'size.title',
						'valueAxis.tickInterval',
						'itemAxis.tickInterval'
					],
					'exclude':[
					]
				},
				usages:[
					'Relationships'
				],
				'supportsMaintainAxisScale': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2marimekko':{
				'type':'Marimekko',
				'doesNotSupportAnnotations': true,
				'aggregateAndSortOnClient':true,
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges':[
					'widget.legend.size'
				],
				'thumbnail': {
					'properties': {
						'itemAxis.gridLines.visible': true,
						'valueAxis.gridLines.visible': true
					}
				},
				'slots':{
					'color':{
						'optional':true,
						'sort':'asc',
						'icon':'colorBy',
						'tags':[
							'ca.dashboard.repeatHere=true',
							'canUseToColor'
						],
						'supportsMaintainAxisScale': true,
						'mldTitleProperty': 'color.title'
					},
					'categories':{
						'optional':false,
						'sort':'asc',
						'icon':'x-axis_bars',
						'mldTitleProperty': 'itemAxis.title'
					},
					'values':{
						'sortable':true,
						'optional':false,
						'multiMeasure':true,
						'clientSideAggregatedAndSortInfo':{
							'mappedSlotCondition':[
								'color'
							],
							'groupOnColumnId':'categories'
						},
						'icon':'yAxis',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.title'
					},
					'width':{
						'sortable':true,
						'optional':true,
						'icon':'width',
						'tags': ['badge']
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						limit: 20,
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						limit: 20,
						'sort':'asc'
					}
				},
				'propertyPrefixes': {
					'dataPoint': 'itemAxis.ticks.labels',
					'width': 'itemAxis.title',
					'values': 'valueAxis.title',
					'categories': 'itemAxis.title',
					'color': 'widget.legend',
					'legend':'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color','valueLabels.color','barLabels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font','valueLabels.font','barLabels.font']
				},
				'properties':{
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'color.title': {
						'multilingual': true
					},
					'effect.duration': {
						'active': false
					},
					'column.border.width':{
						'active':false
					},
					'column.border.color':{
						'active':false
					},
					'valueAxis.minValue': minMaxValidationOptions,
					'valueAxis.maxValue': minMaxValidationOptions,
					'widget.legend.size': {
						'active': false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'contrast.label.color': {
						'defaultValue': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'valueAxis.minValue',
						'valueAxis.maxValue',
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'stacked.percent',
						'itemAxis.title.visible',
						'valueAxis.title.visible',
						'valueAxis.zeroOrigin',
						'valueAxis.logarithmic',
						'itemAxis.gridLines.visible',
						'valueAxis.gridLines.visible',
						'itemAxis.title',
						'valueAxis.title',
						'gridlineColor',
						'itemAxis.ticks.color',
						'valueAxis.ticks.color',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'column.border.width',
						'column.border.color',
						'valueLabels.visible',
						'labelColor',
						'contrast.label.color',
						'label.shadow',
						'effect.duration',
						'optimizeSize',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleFont',
						'widget.legend.titleColor',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.labels.color',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.title.font',
						'valueAxis.title.color',
						'valueLabels.font',
						'valueLabels.color',
						'barLabels.font',
						'barLabels.color',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'colors.color.cat',
						'colors.color.cont',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'color.title',
						'valueAxis.tickInterval'
					],
					'exclude':[
					]
				},
				usages:[
					'Comparison'
				],
				'supportsMaintainAxisScale': false,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2bundlebullet':{
				'type':'Bullet',
				'thumbnail': {
					'properties': {
						'valueAxis.ticks.labels.visible': false
					}
				},
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'doesNotSupportAnnotations': true,
				'doesNotSupportCustomGroupAction': true,
				'slots':{
					'actual':{
						'optional':false,
						'icon':'y-axis_columns',
						'actions': [
							'formatAction',
							'text'
						],
						'tags': ['canUseToColor']
					},
					'goal':{
						'optional':true,
						'icon':'target',
						'actions': [
							'formatAction'
						],
						'tags': ['canUseToColor']
					},
					'min':{
						'optional':true,
						'icon':'values',
						'actions': [
							'formatAction'
						]
					},
					'mid':{
						'optional':true,
						'icon':'values',
						'actions': [
							'formatAction'
						]
					},
					'max':{
						'optional':true,
						'icon':'values',
						'actions': [
							'formatAction'
						]
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc',
						'actions': [
							'sortAction',
							'formatAction',
							'topBottomAction',
							'filter'
						]
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'actions': [
							'sortAction',
							'formatAction',
							'topBottomAction',
							'filter'
						]
					}
				},
				'propertyPrefixes': {
					'legend': 'widget.legend'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont']
				},
				'properties':{
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'chart.title.color': {
						active: false
					},
					'chart.title.auto.areawidth': {
						active: false
					},
					'chart.title.areawidth': {
						active: false
					},
					'chart.title.visible': {
						active: false,
						'defaultValue': false,
						'isReadOnly': true
					},
					'itemAxisSubtitle':{
						'active':false
					},
					'widget.legend.size': {
						'active': false
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'color.min': {
						colorClass: true,
						'addButton': false
					},
					'color.mid': {
						colorClass: true,
						'addButton': false
					},
					'color.max': {
						colorClass: true,
						'addButton': false
					},
					'valueAxis.minValue': minMaxValidationOptions,
					'valueAxis.maxValue': minMaxValidationOptions
				},
				config:{
					'include':[
						'valueAxis.minValue',
						'valueAxis.maxValue',
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'chart.title.color',
						'chart.title.visible',
						'chart.title.areawidth',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'actions',
						'color.min',
						'color.mid',
						'color.max',
						'color.actual',
						'color.goal',
						'itemAxis.title',
						'valueAxisTitleColor',
						'itemAxis.title',
						'itemAxisSubtitle',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'valueAxis.logarithmic',
						'color.actual',
						'color.goal',
						'widget.legend.size',
						'valueAxis.tickInterval',
						'optimizeSize'
					],
					'exclude':[
						'subtitle.visible'
					]
				},
				usages:[
					'Comparison'
				],
				'supportsMaintainAxisScale': false,
				'binning': {
					'auto': false
				},
				'colorPalettes': {
					'single': {
						'color.actual': {
							id: 'defaultPaletteIndex',
							labelId: 'propBarColor',
							defaultColorIndex: 0
						},
						'color.goal': {
							id: 'goal_color',
							labelId: 'propGoalColor',
							defaultColorIndex: 1
						}
					}
				}
			},
			'com.ibm.vis.ravescatter':{
				'type':'Scatter',
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'thumbnail': {
					'properties': {
						'itemAxis.line.visible': true,
						'valueAxis.line.visible': true,
						'itemAxis.gridLines.visible': true,
						'valueAxis.gridLines.visible': true
					}
				},
				'slots':{
					'color':{
						'optional':true,
						'sort':'asc',
						'icon':'colorBy',
						'mldTitleProperty': 'color.title',
						'tags': ['canUseToColor']
					},
					'x':{
						'optional':false,
						'treatOrdinalAsCategory':true,
						'icon':'xAxis',
						'tags':['badge'],
						'mldTitleProperty': 'itemAxis.title',
						'supportSortActionForMeasures': false
					},
					'y':{
						'optional':false,
						'treatOrdinalAsCategory':true,
						'icon':'yAxis',
						'tags':['badge'],
						'mldTitleProperty': 'valueAxis.title',
						'supportSortActionForMeasures': false
					},
					'size':{
						'hidden':true,
						'icon':'size_by'
					},
					'categories':{
						'optional':true,
						'icon':'points'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc',
						'actions': [
							'formatAction',
							'sortAction',
							'topBottomAction'
						]
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc',
						'actions': [
							'formatAction',
							'sortAction',
							'topBottomAction'
						]
					}
				},
				'propertyPrefixes': {
					'dataPoint': 'itemAxis.ticks.labels',
					'x': 'itemAxis.title',
					'y': 'valueAxis.title',
					'color': 'widget.legend',
					'legend':'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color','valueLabels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font','valueLabels.font']
				},
				'properties':{
					'color.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'active': false,
						'defaultValue': LONG_EFFECT_DURATION
					},
					'border.width': {
						'active': false
					},
					'bubble.value.smallest.size': {
						'active': false
					},
					'bubble.value.biggest.size': {
						'active': false
					},
					'valueAxis.minValue': minMaxValidationOptions,
					'valueAxis.maxValue': minMaxValidationOptions,
					'itemAxis.minValue': minMaxValidationOptions,
					'itemAxis.maxValue': minMaxValidationOptions,
					'widget.legend.size': {
						'active': false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'contrast.label.color': {
						'defaultValue': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'valueAxis.minValue',
						'valueAxis.maxValue',
						'itemAxis.minValue',
						'itemAxis.maxValue',
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'gridLines.visible',
						'labels.visible',
						'itemAxis.title.visible',
						'valueAxis.title.visible',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.labels.color',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.title.font',
						'valueAxis.title.color',
						'valueAxis.logarithmic',
						'itemAxis.title',
						'valueAxis.title',
						'gridlineColor',
						'itemAxis.ticks.color',
						'valueAxis.ticks.color',
						'itemAxis.labels.layoutMode',
						'labelColor',
						'contrast.label.color',
						'border.width',
						'border.color',
						'bubble.value.smallest.size',
						'bubble.value.biggest.size',
						'label.shadow',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'symbolShape',
						'effect.duration',
						'optimizeSize',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'valueLabels.font',
						'valueLabels.color',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'colors.categories',
						'colors.color',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'color.title',
						'valueAxis.tickInterval',
						'itemAxis.tickInterval'
					],
					'exclude':[
					]
				},
				usages:[
					'Relationships'
				],
				'supportsMaintainAxisScale': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2bundlepackedbubble':{
				'type':'PackedBubble',
				'disablePan':true,
				'thumbnail': {
					'type': 'img'
				},
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'slots':{
					'categories':{
						'optional':false,
						'icon':'bubbles'
					},
					'color':{
						'optional':true,
						'sort':'asc',
						'icon':'colorBy',
						'supportsMaintainAxisScale': true,
						'mldTitleProperty': 'color.title',
						'tags': ['canUseToColor']
					},
					'size':{
						'optional':true,
						'icon':'size_by',
						'mldTitleProperty': 'size.title'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'propertyPrefixes': {
					'dataPoint': 'labels',
					'categories': 'labels',
					'size': 'widget.legend',
					'color': 'widget.legend',
					'legend': 'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont']
				},
				'properties':{
					'color.title': {
						'multilingual': true
					},
					'size.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'pack':{
						'active':false,
						'defaultValue':'BubbleLayout',
						'isReadOnly': true
					},
					'dataHandling':{
						'active':false,
						'defaultValue':'tabular',
						'isReadOnly': true
					},
					'effect.duration': {
						'active': false,
						'defaultValue': LONG_EFFECT_DURATION
					},
					'widget.legend.size': {
						'active': false
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'contrast.label.color': {
						'defaultValue': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'labels.visible',
						'labels.format',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'effect.duration',
						'labels.color',
						'labels.font',
						'contrast.label.color',
						'label.shadow',
						'pack',
						'dataHandling',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'colors.categories',
						'colors.color',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'color.title',
						'size.title'
					],
					'exclude':[
					]
				},
				'usages':[
					'PartsToWhole'
				],
				'supportsMaintainAxisScale': false,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2bundlehierarchicalpackedbubble':{
				'type':'HierarchicalPackedBubble',
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'disablePan':true,
				'slots':{
					'categories':{
						'optional':false,
						'icon':'bubbles'
					},
					'color':{
						'optional':true,
						'sort':'asc',
						'icon':'colorBy',
						'supportsMaintainAxisScale': true,
						'mldTitleProperty': 'color.title',
						'tags': ['canUseToColor']
					},
					'size':{
						'optional':true,
						'icon':'size_by',
						'mldTitleProperty': 'size.title'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'propertyPrefixes': {
					'dataPoint': 'labels',
					'categories': 'labels',
					'size': 'widget.legend',
					'color': 'widget.legend',
					'legend':'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont']
				},
				'properties':{
					'color.title': {
						'multilingual': true
					},
					'size.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'pack':{
						'active':false,
						'defaultValue':'BubbleLayout',
						'isReadOnly': true
					},
					'dataHandling':{
						'active':false,
						'defaultValue':'hierarchical',
						'isReadOnly': true
					},
					'effect.duration': {
						'active': false,
						'defaultValue': LONG_EFFECT_DURATION
					},
					'widget.legend.size': {
						'active': false
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'contrast.label.color': {
						'defaultValue': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'labels.visible',
						'labels.format',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'effect.duration',
						'labels.color',
						'labels.font',
						'contrast.label.color',
						'label.shadow',
						'pack',
						'dataHandling',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'colors.categories',
						'colors.color',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'color.title',
						'size.title',
						'optimizeSize'
					],
					'exclude':[
					]
				},
				'usages':[
					'PartsToWhole'
				],
				'supportsMaintainAxisScale': false,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2point':{
				'type': 'Point',
				'supportsForecasts':true,
				'aggregateAndSortOnClient':true,
				'thumbnail': {
					'properties': {
						'markers.visible': true,
						'itemAxis.line.visible': true,
						'valueAxis.line.visible': true,
						'itemAxis.gridLines.visible': true,
						'valueAxis.gridLines.visible': true
					}
				},
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'slots':{
					'categories':{
						'optional':false,
						'sort':'asc',
						'icon':'xAxis',
						'tags': ['badge'],
						'mldTitleProperty': 'itemAxis.title'
					},
					'values':{
						'optional':false,
						'sortable':true,
						'multiMeasure':true,
						'clientSideAggregatedAndSortInfo':{
							'mappedSlotCondition':[
								'series'
							],
							'groupOnColumnId':'x'
						},
						'icon':'yAxis',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.title',
						'supportsMaintainAxisScale': true
					},
					'series':{
						'sort':'asc',
						'tags':[
							'ca.dashboard.repeatHere=true',
							'canUseToColor'
						],
						'icon':'colorBy',
						'mldTitleProperty': 'series.title'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'propertyPrefixes': {
					'dataPoint': 'itemAxis.ticks.labels',
					'values': 'valueAxis.title',
					'categories': 'itemAxis.title',
					'series': 'widget.legend',
					'legend': 'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color','valueLabels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font','valueLabels.font']
				},
				'properties':{
					'series.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'lines.visible':{
						'defaultValue':false,
						'active':false,
						'isReadOnly': true
					},
					'format.compact':{
						'active':false
					},
					'markers.shape':{
						'active':false
					},
					'markers.visible': {
						'defaultValue':true,
						'active': false,
						'isReadOnly': true
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration':{
						'active':false
					},
					'valueAxis.minValue': minMaxValidationOptions,
					'valueAxis.maxValue': minMaxValidationOptions,
					'widget.legend.size': {
						'active': false
					},
					'colors.series': {
						'defaultValue': false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'contrast.label.color': {
						'defaultValue': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'valueAxis.minValue',
						'valueAxis.maxValue',
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'gridLines.visible',
						'itemAxis.title.visible',
						'lines.visible',
						'markers.visible',
						'valueAxis.zeroOrigin',
						'valueAxis.logarithmic',
						'valueAxis.title.visible',
						'itemAxis.labels.layoutMode',
						'lineWithPoints.symbol',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.labels.color',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.title.font',
						'valueAxis.title.color',
						'itemAxis.title',
						'valueAxis.title',
						'gridlineColor',
						'itemAxis.ticks.color',
						'valueAxis.ticks.color',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'valueLabels.visible',
						'valueLabels.format',
						'labelColor',
						'contrast.label.color',
						'label.shadow',
						'effect.duration',
						'optimizeSize',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleFont',
						'widget.legend.titleColor',
						'valueLabels.color',
						'valueLabels.font',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'colors.series',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'series.title',
						'valueAxis.tickInterval'
					],
					'exclude':[
						'itemAxis.labels.vertical',
						'line.interpolate'
					]
				},
				'usages':[
					'Trend'
				],
				'supportsMaintainAxisScale': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2bundlewordcloud':{
				'type': 'Wordle',
				'disablePan':true,
				'isRecommendable':false,
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'slots':{
					'categories':{
						'optional':false,
						'sort':'asc',
						'icon':'word'
					},
					'size':{
						'icon':'size_by',
						'mldTitleProperty': 'size.title'
					},
					'color':{
						'icon':'colorBy',
						'sort':'asc',
						'supportsMaintainAxisScale': true,
						'mldTitleProperty': 'color.title',
						'tags': ['canUseToColor']
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'propertyPrefixes': {
					'size': 'widget.legend',
					'color': 'widget.legend',
					'legend': 'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont']
				},
				'properties':{
					'color.title': {
						'multilingual': true
					},
					'size.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'active': false,
						'defaultValue': LONG_EFFECT_DURATION
					},
					'font.padding':{
						'active':false
					},
					'font.minSize':{
						'active':false
					},
					'font.maxSize':{
						'active':false
					},
					'widget.legend.size': {
						'active': false
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					}
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'word.orientation',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'effect.duration',
						'font.padding',
						'font.minSize',
						'font.maxSize',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'colors.color.cat',
						'colors.color.cont',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'color.title',
						'size.title',
						'optimizeSize',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font'
					],
					'exclude':[
					]
				},
				'usages':[
					'Comparison'
				],
				'supportsMaintainAxisScale': false,
				'queryHints': {
					'multiEdgeSort':true
				},
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'list':{
				'type':'List',
				'properties':{
					'format.compact':{
						'active':false
					}
				},
				'binning': {
					'auto': false
				},
				'usages':[
					'TablesAndSummary'
				]
			},
			'com.ibm.vis.rave2line':{
				'type':'Line',
				'supportsForecasts':true,
				'aggregateAndSortOnClient':true,
				'thumbnail': {
					'properties': {
						'markers.visible': true,
						'itemAxis.line.visible': true,
						'valueAxis.line.visible': true,
						'itemAxis.gridLines.visible': true,
						'valueAxis.gridLines.visible': true
					}
				},
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'slots':{
					'categories':{
						'optional':false,
						'sort':'asc',
						'icon':'xAxis',
						'tags': ['badge'],
						'mldTitleProperty': 'itemAxis.title'
					},
					'values':{
						'sortable':true,
						'multiMeasure':true,
						'clientSideAggregatedAndSortInfo':{
							'mappedSlotCondition':[
								'series'
							],
							'groupOnColumnId':'categories'
						},
						'icon':'values',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.title'
					},
					'series':{
						'sort':'asc',
						'tags':[
							'ca.dashboard.repeatHere=true',
							'canUseToColor'
						],
						'icon':'colorBy',
						'mldTitleProperty': 'series.title'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'entryDurationProperty': 'effect.entry.line.duration',
				'propertyPrefixes': {
					'dataPoint': 'itemAxis.ticks.labels',
					'values': 'valueAxis.title',
					'categories': 'itemAxis.title',
					'series': 'widget.legend',
					'legend': 'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color','valueLabels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font','valueLabels.font']
				},
				'properties':{
					'series.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'lines.visible':{
						'defaultValue':true,
						'active':false,
						'isReadOnly': true
					},
					'format.compact':{
						'active':false
					},
					'markers.shape':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'defaultValue': LONG_EFFECT_DURATION,
						'active': false
					},
					'effect.entry.line.type': {
						'public': false
					},
					'effect.entry.line.delay': {
						'active': false,
						'defaultValue': 0
					},
					'effect.entry.line.stagger': {
						'public': false
					},
					'effect.entry.line.duration':{
						'displayMultiplier': 0.001,
						'public': false,
						'defaultValue': SHORT_EFFECT_DURATION
					},
					'valueAxis.minValue': minMaxValidationOptions,
					'valueAxis.maxValue': minMaxValidationOptions,
					'widget.legend.size': {
						active: false
					},
					'itemAxis.line.visible':{
						'active': false,
						'public': false
					},
					'valueAxis.line.visible':{
						'active': false,
						'public': true
					},
					'colors.series': {
						'defaultValue': false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'contrast.label.color': {
						'defaultValue': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'valueAxis.minValue',
						'valueAxis.maxValue',
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'gridLines.visible',
						'itemAxis.title.visible',
						'lines.visible',
						'markers.visible',
						'valueAxis.zeroOrigin',
						'valueAxis.logarithmic',
						'valueAxis.title.visible',
						'itemAxis.labels.layoutMode',
						'lineWithPoints.symbol',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.labels.color',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.title.font',
						'valueAxis.title.color',
						'itemAxis.title',
						'valueAxis.title',
						'gridlineColor',
						'itemAxis.ticks.color',
						'valueAxis.ticks.color',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'valueLabels.visible',
						'valueLabels.format',
						'line.interpolate',
						'labelColor',
						'contrast.label.color',
						'label.shadow',
						'effect.duration',
						'effect.entry.line.type',
						'effect.entry.line.delay',
						'effect.entry.line.stagger',
						'effect.entry.line.duration',
						'optimizeSize',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'valueLabels.font',
						'valueLabels.color',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'colors.series',
						'widget.legend.size',
						'valueAxis.line.visible',
						'itemAxis.line.visible',
						'widget.legend.titleVisible',
						'series.title',
						'valueAxis.tickInterval'
					],
					'exclude':[
					]
				},
				'usages':[
					'Trend'
				],
				'supportsMaintainAxisScale': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2bundleradialbar':{
				'type':'Dial',
				'disablePan':true,
				'isRecommendable':false,
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'slots':{
					'categories':{
						'sort':'asc',
						'icon':'radialbar_category'
					},
					'values':{
						'icon':'values'
					},
					'maxValue':{
						'icon':'values'
					},
					'color':{
						'icon':'colorBy',
						'sort':'asc',
						'mldTitleProperty': 'solor.title',
						'tags': ['canUseToColor']
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'propertyPrefixes': {
					'color': 'widget.legend',
					'legend':'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont']
				},
				'properties':{
					'color.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'active': false,
						'defaultValue': LONG_EFFECT_DURATION
					},
					'dial.startAngle': {
						'active': false
					},
					'dial.maxValue': {
						'active': false
					},
					'dial.border.width':{
						'active': false
					},
					'dial.padding':{
						'active': false
					},
					'dial.width':{
						'active': false
					},
					'valueLabels.size':{
						'active': false
					},
					'valueLabels.fontFamily':{
						'active': false
					},
					'center.background.radius':{
						'active':false
					},
					'center.padding.radius':{
						'active':false
					},
					'center.padding':{
						'active':false
					},
					'dial.minFontSize':{
						'active':false
					},
					'center.radius': {
						active: false
					},
					'widget.legend.size': {
						active: false
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'effect.duration',
						'valueLabels.format',
						'valueLabels.color',
						'valueLabels.size',
						'valueLabels.fontFamily',
						'backRing.color',
						'dial.border.color',
						'dial.border.width',
						'dial.padding',
						'dial.padding.color',
						'dial.width',
						'dial.roundedEnds',
						'dial.startAngle',
						'dial.maxValue',
						'center.background.radius',
						'center.padding.radius',
						'center.color',
						'center.radius',
						'dial.minFontSize',
						'dial.propotionalwidths',
						'dial.showAsPercentage',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'colors.color.cat',
						'colors.color.cont',
						'widget.legend.size',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'widget.legend.titleVisible',
						'color.title',
						'optimizeSize'
					],
					'exclude':[
					]
				},
				'usages':[
					'Comparison',
					'Trend'
				],
				'supportsMaintainAxisScale': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'multipleDialCategoryLabel':{
				'disablePan':true,
				'isRecommendable':false,
				'slots':{
					'category':{
						'optional':false
					},
					'label':{
						'optional':true
					}
				},
				'properties':{
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					}
				},
				'supportsMaintainAxisScale':true
			},
			'com.ibm.vis.rave2bundlecolumn':{
				'type':'Column',
				'supportsForecasts':true,
				'thumbnail': {
					'properties': {
						'valueAxis.gridLines.visible': true
					}
				},
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'aggregateAndSortOnClient':true,
				'slots':{
					'categories':{
						'sort':'asc',
						'icon':'x-axis_bars',
						'tags': ['badge'],
						'mldTitleProperty': 'itemAxis.title'
					},
					'values':{
						'weightedRepeats':true,
						'sortable':true,
						'multiMeasure':true,
						'clientSideAggregatedAndSortInfo':{
							'mappedSlotCondition':[
								'color'
							],
							'groupOnColumnId':'categories'
						},
						'icon':'values',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.title'
					},
					'target':{
						'sortable':true,
						'optional':true,
						'icon':'target',
						'clientSideAggregatedAndSortInfo':{
							'mappedSlotCondition':[
								'color'
							],
							'groupOnColumnId':'categories'
						}
					},
					'color':{
						'sort':'asc',
						'tags':[
							'ca.dashboard.repeatHere=true',
							'canUseToColor'
						],
						'icon':'colorBy',
						'mldTitleProperty': 'color.title'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'entryDurationProperty': 'effect.entry.bar.duration',
				'propertyPrefixes': {
					'dataPoint': 'itemAxis.ticks.labels',
					'values': 'valueAxis.title',
					'categories': 'itemAxis.title',
					'color': 'widget.legend',
					'legend':'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color','valueLabels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font','valueLabels.font']
				},
				'properties':{
					'color.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'bar.direction.vertical':{
						'defaultValue':true,
						'active':false,
						'isReadOnly': true
					},
					'bar.relativePadding': {
						'percentage': true,
						'minValue': 0,
						'maxValue': 0.99,
						'step': 0.01,
						'defaultValue': 0.1
					},
					'widget.legend.size': {
						'active': false
					},
					'stacked':{
						'defaultValue':false,
						'active':false,
						'isReadOnly': true
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'defaultValue': SHORT_EFFECT_DURATION,
						'active': false
					},
					'effect.entry.bar.type': {
						'public': false
					},
					'effect.entry.bar.delay': {
						'active': false,
						'defaultValue': 0
					},
					'effect.entry.bar.stagger': {
						'public': false
					},
					'effect.entry.bar.duration':{
						'displayMultiplier': 0.001,
						'public': false,
						'defaultValue': SHORT_EFFECT_DURATION
					},
					'values.null.color':{
						'active': false
					},
					'column.border.width':{
						'active': false
					},
					'column.border.color':{
						'active': false
					},
					'target.marker.color':{
						'predefinedValue':'customColor1D2473',
						'colorClass': true
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'valueAxis.minValue': minMaxValidationOptions,
					'valueAxis.maxValue': minMaxValidationOptions,
					'contrast.label.color': {
						'defaultValue': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'valueAxis.minValue',
						'valueAxis.maxValue',
						'valueLabels.location',
						'valueLabels.orientation',
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'widget.legend.size',
						'bar.relativePadding',
						'bar.direction.vertical',
						'stacked',
						'stacked.percent',
						'itemAxis.title.visible',
						'valueAxis.title.visible',
						'valueAxis.zeroOrigin',
						'valueAxis.logarithmic',
						'gridLines.visible',
						'itemAxis.labels.layoutMode',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.labels.color',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.title.font',
						'valueAxis.title.color',
						'itemAxis.title',
						'valueAxis.title',
						'gridlineColor',
						'itemAxis.ticks.color',
						'valueAxis.ticks.color',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'column.border.width',
						'column.border.color',
						'values.null.color',
						'valueLabels.visible',
						'valueLabels.format',
						'labelColor',
						'contrast.label.color',
						'label.shadow',
						'effect.duration',
						'effect.entry.bar.type',
						'effect.entry.bar.delay',
						'effect.entry.bar.stagger',
						'effect.entry.bar.duration',
						'optimizeSize',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'valueLabels.font',
						'valueLabels.color',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'target.marker.thickness',
						'target.marker.color',
						'colors.categories',
						'colors.color',
						'widget.legend.titleVisible',
						'color.title',
						'color.title.visible',
						'valueAxis.tickInterval'
					],
					'exclude':[
					]
				},
				'usages':[
					'Comparison'
				],
				'supportsMaintainAxisScale': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2bundlestackedcolumn':{
				'type': 'StackedColumn',
				'aggregateAndSortOnClient':true,
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'thumbnail': {
					'properties': {
						'valueAxis.gridLines.visible': true
					}
				},
				'slots':{
					'categories':{
						'sort':'asc',
						'icon':'x-axis_bars',
						'tags': ['badge'],
						'mldTitleProperty': 'itemAxis.title'
					},
					'values':{
						'weightedRepeats':true,
						'sortable':true,
						'multiMeasure':true,
						'clientSideAggregatedAndSortInfo':{
							'mappedSlotCondition':[
								'color'
							],
							'groupOnColumnId':'categories'
						},
						'icon':'values',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.title'
					},
					'target':{
						'hidden': true
					},
					'color':{
						'useInTopBottomQueries':false,
						'stackValuesOnAxis': true,
						'sort':'asc',
						'tags':[
							'ca.dashboard.repeatHere=true',
							'canUseToColor'
						],
						'icon':'colorBy',
						'mldTitleProperty': 'color.title'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'entryDurationProperty': 'effect.entry.bar.duration',
				'propertyPrefixes': {
					'dataPoint': 'itemAxis.ticks.labels',
					'values': 'valueAxis.title',
					'categories': 'itemAxis.title',
					'color': 'widget.legend',
					'legend':'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color','valueLabels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font','valueLabels.font']
				},
				'properties':{
					'color.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'bar.direction.vertical':{
						'defaultValue':true,
						'active':false,
						'isReadOnly': true
					},
					'bar.relativePadding': {
						'percentage': true,
						'minValue': 0,
						'maxValue': 0.99,
						'step': 0.01,
						'defaultValue': 0.1
					},
					'stacked':{
						'defaultValue':true,
						'active':false,
						'isReadOnly': true
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'defaultValue': SHORT_EFFECT_DURATION,
						'active': false
					},
					'effect.entry.bar.type': {
						'public': false
					},
					'effect.entry.bar.delay': {
						'active': false,
						'defaultValue': 0
					},
					'effect.entry.bar.stagger': {
						'public': false
					},
					'effect.entry.bar.duration':{
						'displayMultiplier': 0.001,
						'public': false,
						'defaultValue': SHORT_EFFECT_DURATION
					},
					'values.null.color':{
						'active': false
					},
					'column.border.width':{
						'active': false
					},
					'column.border.color':{
						'active': false
					},
					'valueAxis.minValue': minMaxValidationOptions,
					'valueAxis.maxValue': minMaxValidationOptions,
					'widget.legend.size': {
						'active': false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'contrast.label.color': {
						'defaultValue': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'valueAxis.minValue',
						'valueAxis.maxValue',
						'valueLabels.location',
						'valueLabels.orientation',
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'bar.relativePadding',
						'bar.direction.vertical',
						'stacked',
						'stacked.percent',
						'itemAxis.title.visible',
						'valueAxis.title.visible',
						'valueAxis.zeroOrigin',
						'valueAxis.logarithmic',
						'gridLines.visible',
						'itemAxis.labels.layoutMode',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.labels.color',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.title.font',
						'valueAxis.title.color',
						'itemAxis.title',
						'valueAxis.title',
						'gridlineColor',
						'itemAxis.ticks.color',
						'valueAxis.ticks.color',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'column.border.width',
						'column.border.color',
						'values.null.color',
						'valueLabels.visible',
						'valueLabels.format',
						'labelColor',
						'contrast.label.color',
						'label.shadow',
						'effect.duration',
						'effect.entry.bar.type',
						'effect.entry.bar.delay',
						'effect.entry.bar.stagger',
						'effect.entry.bar.duration',
						'optimizeSize',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'valueLabels.font',
						'valueLabels.color',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'colors.categories',
						'colors.color',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'color.title',
						'valueAxis.tickInterval'
					],
					'exclude':[
					]
				},
				'slotConfig': {
					'exclude': [
						'yStart'
					]
				},
				'usages':[
					'Comparison'
				],
				'supportsMaintainAxisScale': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2bundlebar':{
				'type': 'Bar',
				'supportsForecasts':true,
				'thumbnail': {
					'properties': {
						'valueAxis.gridLines.visible': true
					}
				},
				'aggregateAndSortOnClient':true,
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'slots':{
					'categories':{
						'sort':'asc',
						'icon':'y-axis_columns',
						'tags': ['badge'],
						'mldTitleProperty': 'itemAxis.title'
					},
					'values':{
						'weightedRepeats':true,
						'sortable':true,
						'multiMeasure':true,
						'clientSideAggregatedAndSortInfo':{
							'mappedSlotCondition':[
								'color'
							],
							'groupOnColumnId':'categories'
						},
						'icon':'values',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.title'
					},
					'target':{
						'sortable':true,
						'optional':true,
						'icon':'target',
						'clientSideAggregatedAndSortInfo':{
							'mappedSlotCondition':[
								'color'
							],
							'groupOnColumnId':'categories'
						}
					},
					'color':{
						'sort':'asc',
						'tags':[
							'ca.dashboard.repeatHere=true',
							'canUseToColor'
						],
						'icon':'colorBy',
						'mldTitleProperty': 'color.title'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'entryDurationProperty': 'effect.entry.bar.duration',
				'propertyPrefixes': {
					'dataPoint': 'itemAxis.ticks.labels',
					'values': 'valueAxis.title',
					'categories': 'itemAxis.title',
					'color': 'widget.legend',
					'legend': 'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color','valueLabels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font','valueLabels.font']
				},
				'properties':{
					'color.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'bar.direction.vertical':{
						'defaultValue':false,
						'active':false,
						'isReadOnly': true
					},
					'bar.relativePadding': {
						'percentage': true,
						'minValue': 0,
						'maxValue': 0.99,
						'step': 0.01,
						'defaultValue': 0.1
					},
					'stacked':{
						'defaultValue':false,
						'active':false,
						'isReadOnly': true
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'defaultValue': SHORT_EFFECT_DURATION,
						'active': false
					},
					'effect.entry.bar.type': {
						'public': false
					},
					'effect.entry.bar.delay': {
						'active': false,
						'defaultValue': 0
					},
					'effect.entry.bar.stagger': {
						'public': false
					},
					'effect.entry.bar.duration':{
						'displayMultiplier': 0.001,
						'public': false,
						'defaultValue': SHORT_EFFECT_DURATION
					},
					'column.border.width':{
						'active': false
					},
					'column.border.color':{
						'active': false
					},
					'values.null.color':{
						'active': false
					},
					'itemAxis.labels.layoutMode': {
						active: false
					},
					'target.marker.color':{
						'predefinedValue':'customColor1D2473',
						'colorClass': true
					},
					'valueAxis.minValue': minMaxValidationOptions,
					'valueAxis.maxValue': minMaxValidationOptions,
					'widget.legend.size': {
						'active': false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'contrast.label.color': {
						'defaultValue': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'valueLabels.location',
						'valueLabels.orientation',
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'bar.relativePadding',
						'bar.direction.vertical',
						'stacked',
						'stacked.percent',
						'itemAxis.title.visible',
						'valueAxis.title.visible',
						'valueAxis.zeroOrigin',
						'valueAxis.logarithmic',
						'gridLines.visible',
						'itemAxis.labels.layoutMode',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.labels.color',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.title.font',
						'valueAxis.title.color',
						'itemAxis.title',
						'valueAxis.title',
						'gridlineColor',
						'itemAxis.ticks.color',
						'valueAxis.ticks.color',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'column.border.width',
						'column.border.color',
						'values.null.color',
						'valueLabels.visible',
						'valueLabels.format',
						'labelColor',
						'contrast.label.color',
						'label.shadow',
						'effect.duration',
						'effect.entry.bar.type',
						'effect.entry.bar.delay',
						'effect.entry.bar.stagger',
						'effect.entry.bar.duration',
						'optimizeSize',
						'valueLabels.font',
						'valueLabels.color',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'target.marker.thickness',
						'target.marker.color',
						'colors.categories',
						'colors.color',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'color.title',
						'valueAxis.tickInterval',
						'valueAxis.minValue',
						'valueAxis.maxValue'
					],
					'exclude':[
					]
				},
				'usages':[
					'Comparison'
				],
				'supportsMaintainAxisScale': true,
				'isItemAxisYAxis': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2bundlestackedbar':{
				'type': 'StackedBar',
				'aggregateAndSortOnClient':true,
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'thumbnail': {
					'properties': {
						'valueAxis.gridLines.visible': true
					}
				},
				'slots':{
					'categories':{
						'sort':'asc',
						'icon':'y-axis_columns',
						'tags': ['badge'],
						'mldTitleProperty': 'itemAxis.title'
					},
					'values':{
						'weightedRepeats':true,
						'sortable':true,
						'multiMeasure':true,
						'clientSideAggregatedAndSortInfo':{
							'mappedSlotCondition':[
								'color'
							],
							'groupOnColumnId':'categories'
						},
						'icon':'values',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.title'
					},
					'target':{
						'hidden': true
					},
					'color':{
						'useInTopBottomQueries':false,
						'stackValuesOnAxis': true,
						'sort':'asc',
						'tags':[
							'ca.dashboard.repeatHere=true',
							'canUseToColor'
						],
						'icon':'colorBy',
						'mldTitleProperty': 'color.title'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'entryDurationProperty': 'effect.entry.bar.duration',
				'propertyPrefixes': {
					'dataPoint': 'itemAxis.ticks.labels',
					'values': 'valueAxis.title',
					'categories': 'itemAxis.title',
					'color': 'widget.legend',
					'legend': 'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color','valueLabels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font','valueLabels.font']
				},
				'properties':{
					'color.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'bar.direction.vertical':{
						'defaultValue':false,
						'active':false,
						'isReadOnly': true
					},
					'bar.relativePadding': {
						'percentage': true,
						'minValue': 0,
						'maxValue': 0.99,
						'step': 0.01,
						'defaultValue': 0.1
					},
					'stacked':{
						'defaultValue':true,
						'active':false,
						'isReadOnly': true
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'defaultValue': SHORT_EFFECT_DURATION,
						'active': false
					},
					'effect.entry.bar.type': {
						'public': false
					},
					'effect.entry.bar.delay': {
						'active': false,
						'defaultValue': 0
					},
					'effect.entry.bar.stagger': {
						'public': false
					},
					'effect.entry.bar.duration':{
						'displayMultiplier': 0.001,
						'public': false,
						'defaultValue': SHORT_EFFECT_DURATION
					},
					'values.null.color':{
						'active': false
					},
					'column.border.width':{
						'active': false
					},
					'column.border.color':{
						'active': false
					},
					'itemAxis.labels.layoutMode': {
						active: false
					},
					'valueAxis.minValue': minMaxValidationOptions,
					'valueAxis.maxValue': minMaxValidationOptions,
					'widget.legend.size': {
						active: false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'contrast.label.color': {
						'defaultValue': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'valueAxis.minValue',
						'valueAxis.maxValue',
						'valueLabels.location',
						'valueLabels.orientation',
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'bar.relativePadding',
						'bar.direction.vertical',
						'stacked',
						'stacked.percent',
						'itemAxis.title.visible',
						'valueAxis.title.visible',
						'valueAxis.zeroOrigin',
						'valueAxis.logarithmic',
						'gridLines.visible',
						'itemAxis.labels.layoutMode',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.labels.color',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.title.font',
						'valueAxis.title.color',
						'itemAxis.title',
						'valueAxis.title',
						'gridlineColor',
						'itemAxis.ticks.color',
						'valueAxis.ticks.color',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'column.border.width',
						'column.border.color',
						'values.null.color',
						'valueLabels.visible',
						'valueLabels.format',
						'labelColor',
						'contrast.label.color',
						'label.shadow',
						'effect.duration',
						'effect.entry.bar.type',
						'effect.entry.bar.delay',
						'effect.entry.bar.stagger',
						'effect.entry.bar.duration',
						'optimizeSize',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'valueLabels.font',
						'valueLabels.color',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'colors.categories',
						'colors.color',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'color.title',
						'valueAxis.tickInterval'
					],
					'exclude':[
					]
				},
				'slotConfig': {
					'exclude': [
						'yStart'
					]
				},
				'usages':[
					'Comparison'
				],
				'supportsMaintainAxisScale': true,
				'isItemAxisYAxis': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2bundlecomposite': {
				'type':'DualAxis',
				'aggregateAndSortOnClient':true,
				'thumbnail': {
					'properties': {
						'itemAxis.gridLines.visible': true,
						'valueAxis.gridLines.visible': true
					}
				},
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'slots':{
					'categories':{
						'sort':'asc',
						'icon':'xAxis',
						'tags': ['badge'],
						'mldTitleProperty': 'itemAxis.title'
					},
					'columnValue':{
						'sortable':true,
						'icon':'values',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.column.title',
						'multiMeasure': true,
						'clientSideAggregatedAndSortInfo':{
							'mappedSlotCondition':[
								'columnColor'
							],
							'groupOnColumnId':'categories'
						}
					},
					'columnColor':{
						'sort':'asc',
						'tags':[
							'ca.dashboard.repeatHere=true',
							'canUseToColor'
						],
						'icon':'colorBy',
						'mldTitleProperty': 'columnColor.title'
					},
					'lineValue':{
						'sortable':true,
						'icon':'values',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.line.title',
						'multiMeasure':true
					},
					'lineColor':{
						'sort':'asc',
						'icon':'colorBy',
						'tags':[
							'ca.dashboard.repeatHere=true',
							'canUseToColor'
						],
						'mldTitleProperty': 'lineColor.title'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'propertyPrefixes': {
					'legend': 'widget.legend',
					'dataPoint': 'itemAxis.ticks.labels',
					'columnValue': 'valueAxis.column.title',
					'categories': 'itemAxis.title',
					'lineValue': 'valueAxis.line.title',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['column.valueLabels.color', 'lineWithPoints.valueLabels.color', 'valueAxis.column.ticks.labels.color','valueAxis.line.ticks.labels.color'],
					'itemAxis.ticks.labels.font': ['column.valueLabels.font', 'lineWithPoints.valueLabels.font', 'valueAxis.column.ticks.labels.font','valueAxis.line.ticks.labels.font'],
					'valueAxis.column.ticks.labels.color': ['valueAxis.line.ticks.labels.color'],
					'valueAxis.column.ticks.labels.font': ['valueAxis.line.ticks.labels.font']
				},
				'properties':{
					'color.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.column.title': {
						'multilingual': true
					},
					'valueAxis.line.title': {
						'multilingual': true
					},
					'format.compact':{
						'active':false
					},
					'markers.shape':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'active': false
					},
					'lineWithPoints.size.min': {
						'active': false
					},
					'lineWithPoints.size.max': {
						'active': false
					},
					'column.borderWidth':{
						'active':false
					},
					'valueAxis.column.zeroOrigin':{
						'active':false,
						'defaultValue':true,
						'isReadOnly': true
					},
					'valueAxis.line.zeroOrigin':{
						'active':false,
						'defaultValue':true,
						'isReadOnly': true
					},
					'widget.legend.size': {
						'active': false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'column.valueLabels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'lineWithPoints.valueLabels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'valueAxis.line.ticks.labels.color': {
						'public': false
					},
					'valueAxis.column.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 4
					},
					'valueAxis.line.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 8
					},
					'valueAxis.column.ticks.labels.color': {
						'colorClass': true,
						'addButton': false,
						'displayPos': 13,
						'caption': 'conditionalFormattingTextColor'
					},
					'bar.relativePadding': {
						'percentage': true,
						'minValue': 0,
						'maxValue': 0.99,
						'step': 0.01,
						'defaultValue': 0.1
					},
					'valueAxis.line.minValue': minMaxValidationOptions,
					'valueAxis.line.maxValue': minMaxValidationOptions,
					'valueAxis.column.minValue': minMaxValidationOptions,
					'valueAxis.column.maxValue': minMaxValidationOptions
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'bar.direction.vertical',
						'lineWithPoints.interpolate',
						'itemAxis.labels.layoutMode',
						'valueAxis.gridLines.visible',
						'itemAxis.gridLines.visible',
						'lineWithPoints.display',
						'lineWithPoints.symbol',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'itemAxis.title.visible',
						'valueAxis.column.title.visible',
						'valueAxis.line.title.visible',
						'valueAxis.column.zeroOrigin',
						'valueAxis.line.zeroOrigin',
						'valueAxis.line.minValue',
						'valueAxis.line.maxValue',
						'valueAxis.column.minValue',
						'valueAxis.column.maxValue',
						'valueAxis.line.logarithmic',
						'valueAxis.column.logarithmic',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.labels.color',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.column.ticks.labels.color',
						'valueAxis.line.ticks.labels.color',
						'valueAxis.column.ticks.labels.font',
						'valueAxis.line.ticks.labels.font',
						'valueAxis.line.title.font',
						'valueAxis.line.title.color',
						'valueAxis.column.title.font',
						'valueAxis.column.title.color',
						'itemAxis.title',
						'valueAxis.column.title',
						'valueAxis.line.title',
						'columnColor.title',
						'lineColor.title',
						'gridlineColor',
						'itemAxis.ticks.color',
						'valueAxis.column.ticks.color',
						'valueAxis.line.ticks.color',
						'column.valueLabels.visible',
						'column.valueLabels.location',
						'column.valueLabels.orientation',
						'lineWithPoints.valueLabels.visible',
						'labelColor',
						'column.valueLabels.color',
						'lineWithPoints.valueLabels.color',
						'column.valueLabels.font',
						'lineWithPoints.valueLabels.font',
						'effect.duration',
						'optimizeSize',
						'useSingleAxis',
						'syncAxes',
						'column.borderColor',
						'column.borderWidth',
						'lineWithPoints.size.min',
						'lineWithPoints.size.max',
						'swapLineColumn',
						'widget.legend.color',
						'widget.legend.titleFont',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleVisible',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'valueAxis.column.ticks.labels.visible',
						'valueAxis.column.ticks.visible',
						'valueAxis.line.ticks.labels.visible',
						'valueAxis.line.ticks.visible',
						'color.column.cat',
						'color.column.cont',
						'color.line',
						'stacked',
						'widget.legend.size',
						'color.title',
						'bar.relativePadding'
					],
					'exclude':[
						'sortX'
					]
				},
				'usages':[
					'Comparison',
					'Trend'
				],
				'supportsMaintainAxisScale': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				},
				'ignoreElementColor': true,
				'isMultilayersWidget': true,
				'supportsLayers': true,
				'layerDescriptions': [
					{
						id: 'column',
						colorRelatedProps: {
							defaultPaletteValue: 'colorPalette0',
							sectionNameId: 'sectionName_colorPalette',
							labelId: 'propColumnPalette',
							contLabelId: 'propColumnHeatPalette',
							defaultColorIndex: {
								labelId: 'propColumnColor',
								defaultValue: 0
							}
						}
					},
					{
						id: 'line',
						colorRelatedProps: {
							defaultPaletteValue: 'colorPalette0',
							sectionNameId: 'sectionName_colorPalette',
							labelId: 'propLineAndSymbolPalette',
							defaultColorIndex: {
								labelId: 'propLineAndSymbolColor',
								defaultValue: 1
							}
						}
					}
				]
			},
			'com.ibm.vis.rave2bundlecomposite.legacy': {
				'type':'DualAxis.legacy',
				'recommendWeight':0.9,
				'thumbnail': {
					'properties': {
						'itemAxis.gridLines.visible': true,
						'valueAxis.gridLines.visible': true
					}
				},
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'slots':{
					'categories':{
						'sort':'asc',
						'icon':'xAxis',
						'tags': ['badge'],
						'mldTitleProperty': 'itemAxis.title'
					},
					'columnValue':{
						'sortable':true,
						'icon':'values',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.column.title'
					},
					'lineValue':{
						'sortable':true,
						'icon':'values',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.line.title'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'propertyPrefixes': {
					'legend': 'widget.legend',
					'dataPoint': 'itemAxis.ticks.labels',
					'columnValue': 'valueAxis.column.title',
					'categories': 'itemAxis.title',
					'lineValue': 'valueAxis.line.title',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color','valueLabels.color','valueAxis.column.ticks.labels.color','valueAxis.line.ticks.labels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font','valueLabels.font','valueAxis.column.ticks.labels.font','valueAxis.line.ticks.labels.font'],
					'valueAxis.column.ticks.labels.color': ['valueAxis.line.ticks.labels.color'],
					'valueAxis.column.ticks.labels.font': ['valueAxis.line.ticks.labels.font']
				},
				'properties':{
					'color.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.column.title': {
						'multilingual': true
					},
					'valueAxis.line.title': {
						'multilingual': true
					},
					'format.compact':{
						'active':false
					},
					'markers.shape':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'optimizeSize':{
						'active':false
					},
					'effect.duration': {
						'active': false
					},
					'lineWithPoints.size': {
						'active': false
					},
					'column.borderWidth':{
						'active':false
					},
					'valueAxis.zeroOrigin':{
						'active':false,
						'defaultValue':true,
						'isReadOnly': true
					},
					'widget.legend.titleVisible': {
						'active':false,
						'defaultValue': false,
						'isReadOnly': true
					},
					'widget.legend.size': {
						'active': false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'valueAxis.line.ticks.labels.color': {
						'public': false
					},
					'valueAxis.column.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 4
					},
					'valueAxis.line.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 8
					},
					'valueAxis.column.ticks.labels.color': {
						'colorClass': true,
						'addButton': false,
						'displayPos': 13,
						'caption': 'conditionalFormattingTextColor'
					},
					'contrast.label.color': {
						'defaultValue': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.legend.display',
						'widget.legend.position',
						'bar.direction.vertical',
						'lines.smooth',
						'itemAxis.labels.layoutMode',
						'gridLines.visible',
						'markers.visible',
						'lineWithPoints.symbol',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'itemAxis.title.visible',
						'valueAxis.title.visible',
						'valueAxis.zeroOrigin',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.labels.color',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'valueAxis.column.ticks.labels.color',
						'valueAxis.line.ticks.labels.color',
						'valueAxis.column.ticks.labels.font',
						'valueAxis.line.ticks.labels.font',
						'valueAxis.line.title.font',
						'valueAxis.line.title.color',
						'valueAxis.column.title.font',
						'valueAxis.column.title.color',
						'itemAxis.title',
						'valueAxis.column.title',
						'valueAxis.line.title',
						'gridlineColor',
						'itemAxis.ticks.color',
						'valueAxis.ticks.color',
						'valueLabels.visible',
						'labelColor',
						'contrast.label.color',
						'label.shadow',
						'effect.duration',
						'optimizeSize',
						'alignAxes',
						'column.borderColor',
						'column.borderWidth',
						'lineWithPoints.size',
						'swapLineColumn',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'widget.legend.titleVisible',
						'valueLabels.font',
						'valueLabels.color',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'color.column',
						'color.line',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'color.title'
					],
					'exclude':[
						'sortX',
						'lineWithPoints.display'
					]
				},
				'usages':[
					'Comparison',
					'Trend'
				],
				'supportsMaintainAxisScale': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				},
				'colorPalettes': {
					'single': {
						'color.column': {
							id: 'defaultPaletteIndex',
							labelId: 'propColumnColor',
							defaultColorIndex: 0
						},
						'color.line': {
							id: 'compositeLineColor',
							labelId: 'propLineAndSymbolColor',
							defaultColorIndex: 1
						}
					}
				}
			},
			'com.ibm.vis.rave2bundlenetwork':{
				'type':'RelationshipGraph',
				'isRecommendable':false
			},
			'com.ibm.vis.rave2network':{
				'type':'Network',
				'doesNotSupportAnnotations': true,
				'doesNotSupportCustomGroupAction': true,
				'supportsMaintainAxisScale':false,
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'slots':{
					'categories':{
						'icon':'from'
					},
					'series':{
						'icon':'to'
					},
					'values':{
						'icon':'lineweight'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'propertyPrefixes': {
					'dataPoint': 'labels',
					'categories': 'widget.legend',
					'series': 'widget.legend',
					'legend':'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont']
				},
				'isRecommendable':false,
				'properties': {
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'effect.duration': {
						'active': false
					},
					'network.nodes.size.min': {
						'active': false
					},
					'network.nodes.size.max': {
						'active': false
					},
					'widget.legend.size': {
						'active': false
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					}
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'labels.visible',
						'effect.duration',
						'optimizeSize',
						'network.nodes.size.min',
						'network.nodes.size.max',
						'network.links.color',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleFont',
						'widget.legend.titleColor',
						'labels.font',
						'labels.color',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'color.from',
						'color.to',
						'widget.legend.size',
						'widget.legend.titleVisible'
					],
					'exclude':[
					]
				},
				'usages':[
					'Relationships'
				],
				'binning': {
					'auto': false
				},
				'colorPalettes': {
					'single':{
						'color.to': {
							id: 'defaultPaletteIndex',
							labelId: 'propToColor',
							defaultColorIndex: 0
						},
						'color.from': {
							id: 'fromColor',
							labelId: 'propFromColor',
							defaultColorIndex: 1
						}
					}
				}
			},
			'com.ibm.vis.rave2bundletiledmap':{
				'type':'Tiledmap',
				'doesNotSupportCustomGroupAction': true,
				'cursor': ['grab', '-moz-grab', '-webkit-grab'],
				'renderWithoutCompletingMapping':true,
				'geo': true,
				'supportsLayers': true,
				'thumbnail': {
					'enabled': false,
					'type': 'img'
				},
				'listenForPropChanges': [
					'region.visible',
					'point.visible',
					'latLong.visible',
					'widget.legend.size'
				],
				'propertiesToSetBeforeData': [
					'widget.data.truncate',
					'point.custom.mapId',
					'point.custom.layerName',
					'point.custom.propertyName',
					'region.custom.mapId',
					'region.custom.layerName',
					'region.custom.propertyName'
				],
				'layerDescriptions':[
					{
						id: 'data.region',
						colorRelatedProps: {
							transparencyProp: 'region.transparency',
							defaultPaletteValue: 'colorPalette0',
							sectionNameId: 'sectionName_regionLayer'
						},
						visibleLayerProps: [
							'region.grouping.mode',
							'region.grouping.heat.weight',
							'region.custom.mapId',
							'region.custom.layerName',
							'region.custom.propertyName'
						]
					},
					{
						id: 'data.point',
						colorRelatedProps: {
							transparencyProp: 'point.transparency',
							defaultPaletteValue: 'colorPalette1',
							sectionNameId: 'sectionName_pointLayer'
						},
						visibleLayerProps: [
							'point.grouping.mode',
							'point.grouping.heat.weight',
							'point.custom.mapId',
							'point.custom.layerName',
							'point.custom.propertyName'
						]
					},
					{
						id: 'data.latlong',
						colorRelatedProps: {
							transparencyProp: 'latLong.transparency',
							defaultPaletteValue: 'colorPalette2',
							sectionNameId: 'sectionName_latLongLayer'
						},
						visibleLayerProps: [
							'latLong.grouping.mode',
							'latLong.grouping.heat.weight'
						]
					}
				],
				//'isActivated':PropertiesHelper.isTiledMapActivated,
				//'getProperitesFromGlobalConfig':      PropertiesHelper.getTiledMapGlobalConfigProperties
				'dataRowLimit':30000,
				'dataRowLimitIE':3000,
				'slots':{
					'locations':{
						'subtype':'location',
						'icon':'location'
					},
					'locationColor':{
						'icon':'heat_map',
						'mldTitleProperty': 'locationColor.title',
						'tags': ['canUseToColor']
					},
					'pointLocations':{
						'icon':'location',
						'subtype':'location'
					},
					'pointSize':{
						'icon':'size_by',
						'mldTitleProperty': 'pointSize.title'
					},
					'pointColor':{
						'icon':'point_heat',
						'mldTitleProperty': 'pointColor.title',
						'tags': ['canUseToColor']
					},
					'latlongLocations.latitude':{
						'subtype':'latitude',
						'useCategoryAsValue':true,
						'icon':'location'
					},
					'latlongLocations.longitude':{
						'subtype':'longitude',
						'useCategoryAsValue':true,
						'icon':'location'
					},
					'latlongLocations.caption':{
						'icon':'text'
					},
					'latlongSize':{
						'icon':'size_by',
						'mldTitleProperty': 'latlongSize.title'
					},
					'latlongColor':{
						'icon':'point_heat',
						'mldTitleProperty': 'latlongColor.title',
						'tags': ['canUseToColor']
					}
				},
				'propertyPrefixes': {
					'locationColor': 'widget.legend',
					'pointColor': 'widget.legend',
					'latlongColor': 'widget.legend',
					'pointSize': 'widget.legend'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont']
				},
				'properties':{
					'locationColor.title': {
						'multilingual': true
					},
					'pointSize.title': {
						'multilingual': true
					},
					'pointColor.title': {
						'multilingual': true
					},
					'latlongSize.title': {
						'multilingual': true
					},
					'latlongColor.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'region.visible': {
						active: false
					},
					'point.visible': {
						active: false
					},
					'latLong.visible': {
						active: false
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'latLong.defaultSize':{
						'active': false,
						'defaultValue':8,
						'isReadOnly': true
					},
					'point.defaultSize': {
						'active': false,
						'defaultValue': 8,
						'isReadOnly': true
					},
					'preserveDrawingBuffer': {
						'defaultValue': true
					},
					'maxZoom': {
						'active': false
					},
					'region.transparency': {
						'active': false
					},
					'point.minSize': {
						'active': false
					},
					'point.maxSize': {
						'active': false
					},
					'point.transparency': {
						'active': false
					},
					'point.staticZeroValueSize': {
						'active': false
					},
					'point.grouping.transition': {
						'active': false
					},
					'point.grouping.cluster.radius': {
						'active': false
					},
					'point.grouping.cluster.groups': {
						'active': false
					},
					'latLong.minSize': {
						'active': false
					},
					'latLong.maxSize': {
						'active': false
					},
					'latLong.transparency': {
						'active': false
					},
					'latLong.staticZeroValueSize': {
						'active': false
					},
					'latLong.grouping.transition': {
						'active': false
					},
					'latLong.grouping.cluster.radius': {
						'active': false
					},
					'latLong.grouping.cluster.groups': {
						'active': false
					},
					'Mapbox.secretToken':{
						'active':false
					},
					'Mapbox.accountName':{
						'active':false
					},
					'Mapbox.token':{
						'active':false
					},
					'widget.legend.size': {
						'active': false
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'mapStyle': {
						active: false
					}
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'mapStyle',
						'map.style.2',
						'map.style.url',
						'autoZoom',
						'maxZoom',
						'values.null.color',
						'enableLayerControl',
						'enableFeatureSearchControl',
						'region.visible',
						'region.transparency',
						'region.custom.mapId',
						'region.custom.layerName',
						'region.custom.propertyName',
						'point.visible',
						'point.defaultSize',
						'point.minSize',
						'point.maxSize',
						'point.transparency',
						'point.staticZeroValueSize',
						'point.custom.mapId',
						'point.custom.layerName',
						'point.custom.propertyName',
						'point.grouping.mode',
						'point.grouping.transition',
						'point.grouping.heat.weight',
						'point.grouping.cluster.radius',
						'point.grouping.cluster.groups',
						'latLong.defaultSize',
						'latLong.visible',
						'latLong.minSize',
						'latLong.maxSize',
						'latLong.transparency',
						'latLong.staticZeroValueSize',
						'latLong.grouping.mode',
						'latLong.grouping.transition',
						'latLong.grouping.heat.weight',
						'latLong.grouping.cluster.radius',
						'latLong.grouping.cluster.groups',
						'Mapbox.secretToken',
						'Mapbox.accountName',
						'Mapbox.token',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'interactivityState',
						'locationColor',
						'locationColorCat',
						'pointColor',
						'pointColorCat',
						'latlongColor',
						'latlongColorCat',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'locationColor.title',
						'pointSize.title',
						'pointColor.title',
						'latlongSize.title',
						'latlongColor.title',
						'optimizeSize',
						'mapLabels.localLanguage'
					],
					'exclude':[
					]
				},
				'usages':[
					'Geospatial'
				],
				'supportsMaintainAxisScale': true,
				'ignoreElementColor': true,
				'isMultilayersWidget': true,
				'binning': {
					'auto': false
				},
				'requiredConfig':['Mapbox.secretToken','Mapbox.accountName','Mapbox.token']
			},
			'com.ibm.vis.rave2polygonmap':{
				'type':'Polygonmap',
				'doesNotSupportAnnotations': true,
				'doesNotSupportCustomGroupAction': true,
				'geo': true,
				'thumbnail': {
					'type': 'img'
				},
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'slots':{
					'categories':{
						'subtype':'location',
						'icon':'location',
						'recommendMulti':true
					},
					'values':{
						'icon':'heat_map'
					},
					'size':{
						'icon':'size_by'
					},
					'pointcolor':{
						'icon':'point_heat',
						'mldTitleProperty': 'pointcolor.title',
						'tags': ['canUseToColor']
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'propertyPrefixes': {
					'legend': 'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont']
				},
				'properties':{
					'pointcolor.title': {
						'multilingual': true
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'active': false
					},
					'widget.legend.size': {
						'active': false
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					}
				},
				config:{
					'include':[
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleFont',
						'widget.legend.titleColor',
						'labels.visible',
						'values.null.color',
						'effect.duration',
						'optimizeSize',
						'autoZoom',
						'interactivityState',
						'colors.categories',
						'colors.values',
						'colors.pointcolor',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'pointcolor.title',
						'size.title',
						'values.title',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font'
					],
					'exclude':[
					]
				},
				'usages':[
					'Geospatial'
				],
				'supportsMaintainAxisScale': true,
				'binning': {
					'auto': false
				}
			},
			'com.ibm.vis.rave2bundleradar':{
				'type': 'Radar',
				'supportsForecasts':true,
				'ignoreElementColor': false,
				'isRecommendable': false,
				'thumbnail': {
					'properties': {
						'valueAxis.gridLines.visible': true
					}
				},
				'aggregateAndSortOnClient':true,
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'listenForPropChanges': [
					'widget.legend.size'
				],
				'slots':{
					'categories':{
						'sort':'asc',
						'icon':'y-axis_columns',
						'tags': ['badge'],
						'mldTitleProperty': 'itemAxis.title'
					},
					'series':{
						'sort':'asc',
						'tags':[
							'ca.dashboard.repeatHere=true',
							'canUseToColor'
						],
						'icon':'colorBy',
						'mldTitleProperty': 'color.title'
					},
					'values':{
						'weightedRepeats':true,
						'sortable':true,
						'multiMeasure':false,
						'clientSideAggregatedAndSortInfo':{
							'mappedSlotCondition':[
								'color'
							],
							'groupOnColumnId':'categories'
						},
						'icon':'values',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.title'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'entryDurationProperty': 'effect.entry.bar.duration',
				'propertyPrefixes': {
					'dataPoint': 'itemAxis.ticks.labels',
					'values': 'valueAxis.title',
					'categories': 'itemAxis.title',
					'color': 'widget.legend',
					'legend': 'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color','valueLabels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font','valueLabels.font']
				},
				'properties':{
					'color.title': {
						'multilingual': true
					},
					'colors.series': {
						'defaultValue': false
					},
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'format.compact':{
						'active':false
					},
					'legendPosition':{
						'active':false
					},
					'legend.display':{
						'active':false
					},
					'effect.duration': {
						'defaultValue': SHORT_EFFECT_DURATION,
						'active': false
					},
					'column.border.color':{
						'active': false
					},
					'valueAxis.minValue': {
						checkForValidValue: validateNumIsFloatOrNull,
						'supportsValidationChecks': true,
						'validationInfo': {
							messageId: 'customRangeErrorMessage'
						}
					},
					'valueAxis.maxValue': {
						checkForValidValue: validateNumIsFloatOrNull,
						'supportsValidationChecks': true,
						'validationInfo': {
							messageId: 'customRangeErrorMessage'
						}
					},
					'widget.legend.size': {
						'active': false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					}

				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'optimizeSize',
						'colors.series',
						'widget.data.truncate',
						'widget.iterator',
						'widget.legend.display',
						'widget.legend.position',
						'widget.legend.titleVisible',
						'widget.legend.titleFont',
						'widget.legend.titleColor',
						'widget.legend.font',
						'widget.legend.titleFont',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.titleColor',
						'widget.legend.size',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'itemAxis.ticks.color',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.labels.color',
						'itemAxis.line.visible',
						'itemAxis.line.color',
						'itemAxis.gridLines.visible',
						'itemAxis.gridLines.color',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'valueAxis.ticks.color',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'valueAxis.line.visible',
						'valueAxis.line.color',
						'valueAxis.gridLines.visible',
						'valueAxis.gridLines.color',
						'valueAxis.minValue',
						'valueAxis.maxValue',
						'valueAxis.zeroOrigin',
						'valueAxis.logarithmic',
						'lineWithPoints.symbol',
						'lineWithPoints.display',
						'lineWithPoints.size',
						'polygons.filled',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'effect.duration',
						'effect.entry.bar.type',
						'effect.entry.bar.delay',
						'effect.entry.bar.stagger',
						'effect.entry.bar.duration',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font'
					],
					'exclude':[
					]
				},
				'usages':[
					'Comparison'
				],
				'supportsMaintainAxisScale': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2bundlewaterfall':{
				'type':'Waterfall',
				'isRecommendable': false,
				'ignoreElementColor': true,
				'doesNotSupportCategoricalPalette': true,
				'thumbnail': {
					'properties': {
						'valueAxis.gridLines.visible': true
					}
				},
				'listenForPropChanges':[
					'widget.legend.size'
				],
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'aggregateAndSortOnClient':true,
				'slots':{
					'categories':{
						'sort':'asc',
						'icon':'x-axis_bars',
						'tags': ['badge'],
						'mldTitleProperty': 'itemAxis.title'
					},
					'subcategories':{
						'tags': ['ca.dashboard.repeatHere=true' ]
					},
					'values':{
						'weightedRepeats':true,
						'sortable':true,
						'multiMeasure':true,
						'clientSideAggregatedAndSortInfo':{
							'mappedSlotCondition':[
								'color'
							],
							'groupOnColumnId':'categories'
						},
						'icon':'values',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.title'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'entryDurationProperty': 'effect.entry.bar.duration',
				'propertyPrefixes': {
					'dataPoint': 'itemAxis.ticks.labels',
					'values': 'valueAxis.title',
					'categories': 'itemAxis.title',
					'color': 'widget.legend',
					'legend':'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color','valueLabels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font','valueLabels.font']
				},
				'properties':{
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'bar.direction.vertical':{
						'defaultValue':true,
						'active':false,
						'isReadOnly': true
					},
					'stacked':{
						'defaultValue':false,
						'active':false,
						'isReadOnly': true
					},
					'format.compact':{
						'active':false
					},
					'valueAxis.zeroOrigin':{
						'defaultValue':true
					},
					'effect.duration': {
						'defaultValue': SHORT_EFFECT_DURATION,
						'active': false
					},
					'effect.entry.bar.type': {
						'public': false
					},
					'effect.entry.bar.delay': {
						'active': false,
						'defaultValue': 0
					},
					'effect.entry.bar.stagger': {
						'public': false
					},
					'effect.entry.bar.duration':{
						'displayMultiplier': 0.001,
						'public': false,
						'defaultValue': SHORT_EFFECT_DURATION
					},
					'values.null.color':{
						'active': false
					},
					'column.border.width':{
						'active': false
					},
					'column.border.color':{
						'active': false
					},
					'widget.repeatHorizontal.labels.font':{
						'active':false
					},
					'widget.repeatVertical.labels.font':{
						'active':false
					},
					'totalColumn.positive.color':{
						'colorClass': true
					},
					'totalColumn.negative.color':{
						'colorClass': true
					},
					'firstColumn.positive.color':{
						'predefinedValue': 'null', // initially deactive
						'colorClass': true
					},
					'firstColumn.negative.color':{
						'predefinedValue': 'null', // initially deactive
						'colorClass': true
					},
					'column.positive.color':{
						'colorClass': true
					},
					'column.negative.color':{
						'colorClass': true
					},
					'widget.legend.size': {
						'active': false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.minValue': minMaxValidationOptions,
					'valueAxis.maxValue': minMaxValidationOptions,
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'totalColumn.label': {
						'multilingual': true
					}
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'backgroundColor',
						'widget.legend.backgroundColor',
						'optimizeSize',
						'widget.data.truncate',
						'widget.iterator',
						'itemAxis.title.visible',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'itemAxis.labels.layoutMode',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.color',
						'itemAxis.ticks.labels.color',
						'itemAxis.line.visible',
						'itemAxis.gridLines.visible',
						'itemAxis.title',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.title.visible',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'valueAxis.ticks.color',
						'valueAxis.line.visible',
						'valueAxis.gridLines.visible',
						'valueAxis.zeroOrigin',
						'valueAxis.title',
						'valueAxis.title.font',
						'valueAxis.title.color',
						'valueAxis.ticks.labels.font',
						'valueAxis.minValue',
						'valueAxis.maxValue',
						'valueAxis.ticks.labels.color',
						'valueLabels.visible',
						'valueLabels.location',
						'valueLabels.orientation',
						'totalColumn.visible',
						'totalColumn.label',
						'column.border.width',
						'column.border.color',
						'transpose',
						'effect.duration',
						'widget.legend.display',
						'widget.legend.position',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleFont',
						'widget.legend.titleColor',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'totalColumn.legend.label.positive',
						'totalColumn.legend.label.negative',
						'firstColumn.positive.label',
						'firstColumn.negative.label',
						'value.positive.label',
						'value.negative.label',
						'totalColumn.positive.color',
						'totalColumn.negative.color',
						'firstColumn.positive.color',
						'firstColumn.negative.color',
						'column.positive.color',
						'column.negative.color',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'valueAxis.tickInterval',
						'subcategory.totals',
						'subcategory.values'
					],
					'exclude':[
					]
				},
				'usages':[
					'Trend'
				],
				'supportsMaintainAxisScale': true,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.rave2bundleboxplot':{
				'type':'BoxPlot',
				'isRecommendable': true,
				'ignoreElementColor': true,
				'thumbnail': {
					'properties': {
						'valueAxis.gridLines.visible': true
					}
				},
				'listenForPropChanges':[
					'widget.legend.size'
				],
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'slots':{
					'categories':{
						'sort':'asc',
						'icon':'x-axis_bars',
						'tags': ['badge'],
						'mldTitleProperty': 'itemAxis.title'
					},
					'values':{
						'weightedRepeats':true,
						'sortable':true,
						'multiMeasure':true,
						'clientSideAggregatedAndSortInfo':{
							'mappedSlotCondition':[
								'color'
							],
							'groupOnColumnId':'categories'
						},
						'icon':'values',
						'tags': ['badge'],
						'mldTitleProperty': 'valueAxis.title'
					},
					'color':{
						'sort':'asc',
						'tags':[
							'ca.dashboard.repeatHere=true',
							'canUseToColor'
						],
						'icon':'colorBy',
						'mldTitleProperty': 'color.title'
					},
					'key':{
						'sort':'asc',
						'icon':'key'
					},
					'repeatHorizontal':{
						'icon':'multiplier_h',
						'sort':'asc'
					},
					'repeatVertical':{
						'icon':'multiplier_v',
						'sort':'asc'
					}
				},
				'entryDurationProperty': 'effect.entry.bar.duration',
				'propertyPrefixes': {
					'dataPoint': 'itemAxis.ticks.labels',
					'values': 'valueAxis.title',
					'categories': 'itemAxis.title',
					'color': 'widget.legend',
					'legend':'widget.legend',
					'row': 'widget.repeatVertical.labels',
					'column': 'widget.repeatHorizontal.labels'
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont'],
					'itemAxis.ticks.labels.color': ['valueAxis.ticks.labels.color','valueLabels.color'],
					'itemAxis.ticks.labels.font': ['valueAxis.ticks.labels.font','valueLabels.font']
				},
				'properties':{
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					},
					'itemAxis.title': {
						'multilingual': true
					},
					'valueAxis.title': {
						'multilingual': true
					},
					'format.compact':{
						'active':false
					},
					'effect.duration': {
						'defaultValue': SHORT_EFFECT_DURATION,
						'active': false
					},
					'effect.entry.bar.type': {
						'public': false
					},
					'effect.entry.bar.delay': {
						'active': false,
						'defaultValue': 0
					},
					'effect.entry.bar.stagger': {
						'public': false
					},
					'effect.entry.bar.duration':{
						'displayMultiplier': 0.001,
						'public': false,
						'defaultValue': SHORT_EFFECT_DURATION
					},
					'values.null.color':{
						'active': false
					},
					'column.border.width':{
						'active': false
					},
					'column.border.color':{
						'active': false
					},
					'itemAxis.ticks.labels.font':{
						'active':false
					},
					'valueAxis.ticks.labels.font':{
						'active':false
					},
					'itemAxis.title.font':{
						'active':true
					},
					'valueAxis.title.font':{
						'active':true
					},
					'valueLabels.font':{
						'active':false
					},
					'widget.repeatHorizontal.labels.font':{
						'active':false
					},
					'widget.repeatVertical.labels.font':{
						'active':false
					},
					'widget.legend.size': {
						'active': false
					},
					'itemAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'values.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.title.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'itemAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'valueAxis.ticks.labels.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					},
					'valueAxis.minValue': minMaxValidationOptions,
					'valueAxis.maxValue': minMaxValidationOptions
				},
				'queryHints':{
					'multiEdgeSort':true
				},
				config:{
					'include':[
						'backgroundColor',
						'widget.legend.backgroundColor',
						'optimizeSize',
						'widget.data.truncate',
						'widget.iterator',
						'itemAxis.title.visible',
						'itemAxis.ticks.labels.visible',
						'itemAxis.ticks.visible',
						'itemAxis.labels.layoutMode',
						'itemAxis.ticks.labels.font',
						'itemAxis.ticks.color',
						'itemAxis.ticks.labels.color',
						'itemAxis.line.visible',
						'itemAxis.gridLines.visible',
						'itemAxis.title',
						'itemAxis.title.font',
						'itemAxis.title.color',
						'valueAxis.title',
						'valueAxis.title.font',
						'valueAxis.title.color',
						'valueAxis.title.visible',
						'valueAxis.ticks.labels.visible',
						'valueAxis.ticks.visible',
						'valueAxis.ticks.color',
						'valueAxis.line.visible',
						'valueAxis.gridLines.visible',
						'valueAxis.zeroOrigin',
						'valueAxis.ticks.labels.font',
						'valueAxis.ticks.labels.color',
						'valueAxis.tickInterval',
						'valueAxis.minValue',
						'valueAxis.maxValue',
						'valueLabels.visible',
						'valueLabels.location',
						'valueLabels.orientation',
						'totalColumn.visible',
						'transpose',
						'effect.duration',
						'widget.legend.color',
						'widget.legend.splitter',
						'widget.legend.font',
						'widget.legend.titleFont',
						'widget.legend.titleColor',
						'widget.repeatHorizontal.labels.color',
						'widget.repeatVertical.labels.color',
						'widget.repeatHorizontal.labels.font',
						'widget.repeatVertical.labels.font',
						'widget.legend.size',
						'widget.legend.titleVisible',
						'colors.color',
						'valueAxis.logarithmic',
						'data.handling',
						'widget.legend.display',
						'widget.legend.position'
					],
					'exclude':[
					]
				},
				'usages':[
					'Trend'
				],
				'supportsMaintainAxisScale': false,
				'binning': {
					'auto': true,
					'defaultNumberOfBins': 10
				}
			},
			'com.ibm.vis.schematicsPreview': {
				'isSchematic': true,
				'type': 'com.ibm.vis.schematicsPreview',
				'doesNotSupportCustomGroupAction': true,
				'cursor': ['grab', '-moz-grab', '-webkit-grab'],
				'renderWithoutCompletingMapping':true,
				'thumbnail': {
					'enabled': false,
					'type': 'img'
				},
				'listenForPropChanges': [
					'schematic',
					'showPreview',
					'region.visible',
					'point.visible',
					'views.hidden'
				],
				'dataRowLimit':30000,
				'dataRowLimitIE':3000,
				'slots':{
					'locations':{
						'subtype':'location',
						'icon':'location'
					},
					'locationColor':{
						'icon':'heat_map'
					},
					'pointLocations':{
						'icon':'location',
						'subtype':'location'
					},
					'pointSize':{
						'icon':'size_by',
						'multiMeasure':true
					},
					'pointColor':{
						'icon':'point_heat',
						'tags':[
							'ca.dashboard.repeatHere=true'
						]
					}
				},
				'groupedProperty': {
					'widget.legend.color': ['widget.legend.titleColor'],
					'widget.legend.font': ['widget.legend.titleFont']
				},
				'propertiesToSetBeforeData': [
					'widget.data.truncate'
				],
				'propertyPrefixes': {
					'locationColor': 'widget.legend',
					'pointColor': 'widget.legend',
					'pointSize': 'widget.legend'
				},
				'properties': {
					'widget.data.truncate': {
						'active': false,
						'defaultValue': true
					},
					'widget.iterator': {
						active: false,
						'defaultValue': false
					},
					'debug.mode': {
						'active': true,
						'defaultValue': true
					},
					'schematicLibrary': {
						'active': false
					},
					'schematic': {
						'active': false
					},
					'backgroundColor': {
						'active': false
					},
					'showPreview': {
						'active': false
					},
					'region.visible': {
						'active': false
					},
					'point.visible': {
						'active': false
					},
					'widget.legend.color':{
						'colorClass': true,
						'addButton': false,
						'displayPos': 45
					},
					'widget.legend.titleColor': {
						'colorClass': true,
						'active': false
					}
				},
				'supportsLayers': true,
				'layerDescriptions':[
					{
						id: 'data.region',
						colorRelatedProps: {
							transparencyProp: 'region.transparency',
							defaultPaletteValue: 'colorPalette0',
							sectionNameId: 'sectionName_regionLayer'
						},
						visibleLayerProps: [
							'region.visible',
							'region.transparency'
						]
					},
					{
						id: 'data.point',
						colorRelatedProps: {
							transparencyProp: 'point.transparency',
							defaultPaletteValue: 'colorPalette1',
							sectionNameId: 'sectionName_pointLayer'
						},
						visibleLayerProps: [
							'point.visible',
							'point.transparency'
						]
					}
				],
				'supportsMaintainAxisScale': true,
				'ignoreElementColor': true,
				'isMultilayersWidget': true,
				'binning': {
					'auto': false
				},
				'config': {
					'include': [
						'widget.data.truncate',
						'widget.iterator',
						'debug.mode',
						'schematicLibrary',
						'schematic',
						'backgroundColor',
						'widget.legend.backgroundColor',
						'optimizeSize',
						'showPreview',
						'region.visible',
						'point.visible',
						'colors.location.cat',
						'colors.location.cont',
						'colors.point.cat',
						'colors.point.cont',
						'zoomView',
						'zoomData',
						'viewTransparency',
						'enableViewControl',
						'enableLayerControl',
						'region.transparency',
						'point.transparency',
						'point.sizeScaling',
						'point.defaultSize',
						'point.minSize',
						'point.maxSize',
						'point.staticZeroValueSize',
						'point.autoScaleFactor',
						'widget.legend.display',
						'widget.legend.position',
						'widget.legend.size',
						'widget.legend.splitter',
						'locationColor.title',
						'pointColor.title',
						'pointSize.title',
						'widget.legend.titleVisible',
						'widget.legend.titleColor',
						'widget.legend.titleFont',
						'widget.legend.font',
						'widget.legend.fontSize',
						'widget.legend.color',
						'widget.legend.splitter'
					]
				}
			},
			'visualizationPreview':{
				'type': 'visualizationPreview',
				'isCustomVis': true,
				'aggregateAndSortOnClient':true,
				'queryHints':{
					'multiEdgeSort':true
				},
				'includeAllProperties': true,
				'ignoreElementColor': true,
				'listenForPropChanges': [],
				'slots': {
					'color': {
						'sort': 'asc',
						'icon': 'colorBy'
					},
					'repeatHorizontal': {
						'icon': 'multiplier_h',
						'sort': 'asc'
					},
					'repeatVertical': {
						'icon': 'multiplier_v',
						'sort': 'asc'
					}
				},
				'properties': {
					'widget.data.truncate': {
						active: false,
						defaultValue: true
					},
					'widget.iterator': {
						active: false,
						defaultValue: false
					}
				},
				'bundleInclude': [],
				'config': {
					'include': [
						'widget.data.truncate',
						'widget.iterator'
					]
				}
			}
		},

		// --- METHODS
		setAllViprConfigProperties: (viprConfigDefinition, options) => {
			viprConfigDefinition.bundleInclude = [];
			viprConfigDefinition.config = viprConfigDefinition.config ? viprConfigDefinition.config : {};
			viprConfigDefinition.properties = viprConfigDefinition.properties ? viprConfigDefinition.properties: {};
			viprConfigDefinition.listenForPropChanges = [];
			viprConfigDefinition.supportsMaintainAxisScale = false;
			viprConfigDefinition.renderWithoutCompletingMapping = false;
			if(options.dataSets && options.dataSets.length === 0) {
				viprConfigDefinition.renderWithoutCompletingMapping = true;
			}
			const viprProperties = options.viprProperties ? options.viprProperties : [];
			viprConfigDefinition.groupedProperty = options.viprGroupProperties ? options.viprGroupProperties : {};
			for(let i = 0; i < viprProperties.length; i++) {
				let property = viprProperties[i];
				let tempProp = {};
				if (property.name === 'maintainAxisScales' && property.active) {
					viprConfigDefinition.supportsMaintainAxisScale = true;
				} else if (property.name === 'widget.data.truncate') {
					viprConfigDefinition.propertiesToSetBeforeData = ['widget.data.truncate'];
				}
				//We need this for schematic property that values has to be overridden after render
				if (property.type === 'enum' && property.dynamic) {
					viprProperties[i] = _.clone(viprProperties[i]);
					delete viprProperties[i].defaultValue;
					delete viprProperties[i].possibleValues;
				} else if (property.type === 'font') {
					viprProperties[i] = _.clone(viprProperties[i]);
					viprProperties[i].defaultValue = property.defaultValue && property.defaultValue.toString ? property.defaultValue.toString() : null;
				} else if ( property.type === 'color') {
					viprProperties[i] = _.clone(viprProperties[i]);
					viprProperties[i].colorClass = true;
				} else if (property.type === 'number') {
					viprProperties[i] = _.clone(viprProperties[i]);
					viprProperties[i].checkForValidValue = validateNumIsFloatOrNull;
					if (property.supportsValidationChecks === undefined) {
						viprProperties[i].supportsValidationChecks = true;
					}
					if (property.validationInfo === undefined) {
						viprProperties[i] = _.clone(viprProperties[i]);
						viprProperties[i].validationInfo = {
							supportsFloats: true,
							messageId: 'propNumberValidationErrorMessageId'
						};
					}
				}

				property = viprProperties[i];

				_.forEach(property, (value, key) => {
					tempProp[key] = value;
				});
				if (!viprConfigDefinition.properties[property.name]) {
					viprConfigDefinition.properties[property.name] = tempProp;
				}
				const isIncluded = (property.visibility !== 'excluded') && property.visibility !== 'configuration';
				if (isIncluded && viprConfigDefinition.config.include.indexOf( property.name ) === -1 ) { // do not explose config props in the UI 297936
					viprConfigDefinition.config.include.push(property.name);
				}
				if (viprConfigDefinition.bundleInclude.indexOf( property.name ) === -1 ) {
					viprConfigDefinition.bundleInclude.push(property.name);
				}
			}
		},

		getConfig: function(id) {
			return customVisConfigurations[id] || this.configurations[id];
		},

		/**
		 * Extend the VIPRConfig configuration with the optional configuration defined in the viprLib.
		 * This is typically used by application integrators (ie. PA) to contribute their own vipr vis
		 * along with VIPRConfig configuration to declare properties.
		 * 	{
		 * 		"containerId": "com.ibm.bi.dashboard.vipr.visLibrary",
		 * 		"id": "rave2Bundle",
		 * 		"visId": "com.ibm.vis.rave2Bundle",
		 * 		"location": "vizbundles/rave2Bundle",
		 * 		"icon": "visualizations-rave2",
		 * 		"placeholderIcon": "placeholders/rave2.svg",
		 * 		"name": "Rave 2",
		 * 		"configuration": {
		 * 			"properties": {
		 * 				"ravespec": {
		 * 					"active": true,
		 * 					"isReadOnly": true
		 * 				}
		 * 			},
		 * 			"config": {
		 * 				"include": [ "ravespec" ]
		 * 			}
		 * 		}
		 * 	}
		 * @param {Object} definition vipr library definition declared in viprLib
		 */
		extendConfig: function(definition) {
			if (definition && definition.id && definition.configuration) {
				const id = definition.id;
				const source = definition.configuration;
				const target = this.configurations[id];
				if (!target) {
					this.configurations[id] = source;
				} else {
					// limit the extension to property related configurations for now
					if (source.config) {
						if (source.config.include) {
							target.config.include.push(...source.config.include);
						}
						if (source.config.exclude) {
							target.config.exclude.push(...source.config.exclude);
						}
					}
					if (source.properties) {
						_.extend(target.properties, source.properties);
					}
				}
			}
		},

		addCustomVisConfiguration: function(libConfigDefinition) {
			const isSchematicPreview = libConfigDefinition.id === 'com.ibm.vis.schematicsPreview';
			const isVisualizationPreview = libConfigDefinition.id === 'visualizationPreview';

			if (isSchematicPreview) {
				this._addCustomVisConfiguration(this.getConfig(libConfigDefinition.id ), libConfigDefinition);
			} else if (libConfigDefinition.isSchematic && !isSchematicPreview) {
				//clone the config
				let schematicConfig = JSON.parse(JSON.stringify(DEFAULT_SCHEMATIC_VIS_CONFIGURATION['customVis']));
				this._setSchematicLib(schematicConfig, libConfigDefinition);
			} else if(libConfigDefinition.isCustomVis && !isVisualizationPreview) {
				//clone the config
				let customConfig = JSON.parse(JSON.stringify(DEFAULT_CUSTOM_VIS_CONFIGURATION['customVis']));
				customVisConfigurations[libConfigDefinition.id] = _.extend(customConfig, libConfigDefinition);
			} else if (isVisualizationPreview) {
				let previewConfig = JSON.parse(JSON.stringify(this.configurations[libConfigDefinition.id]));
				customVisConfigurations[libConfigDefinition.id] = _.extend(previewConfig, libConfigDefinition);
			}
		},

		_setSchematicLib(schematicConfig, libConfigDefinition) {
			if ( !schematicConfig.config.override ) {
				schematicConfig.config.override = {};
			}
			// set the schematicLibrary library property - this has to be set at runtime
			schematicConfig.config.override['schematicLibrary'] = { defaultValue: libConfigDefinition.id };
			this._addCustomVisConfiguration(schematicConfig, libConfigDefinition);
		},

		_addCustomVisConfiguration(schematicConfig, libConfigDefinition) {
			customVisConfigurations[libConfigDefinition.id] = _.extend(schematicConfig, libConfigDefinition);
			customVisConfigurations[libConfigDefinition.id].schematicVis = new SchematicVis();
		},

		removeCustomVisConfiguration: function(visId) {
			if (customVisConfigurations[visId] && customVisConfigurations[visId].schematicVis) {
				customVisConfigurations[visId].schematicVis.destroy();
			}
			delete customVisConfigurations[visId];
		}
	});
	return new VIPRConfig();
});
